import SendIcon from '@/components/icons/SendIcon';
import {
  banPresenceParticipant,
  deleteWorkspaceChatMessage,
  fetchChatHistory,
  fetchPublicOffer,
  fetchPublicQuiz,
  fetchPublicResource,
  fetchPublicResourceState,
  fetchPublicSurvey,
  fetchWorkspaceChatMessages,
  fetchWorkspaceQuestions,
  moveWorkspaceMessageToQuestions,
  pinWorkspaceChatMessage,
  postAnalyticsEvent,
  postAttendeeChatMessage,
  postChatReaction,
  postWorkspaceChatMessage,
  unpinWorkspaceChatMessage,
  validateWorkspaceQuestion,
} from '@/features/live/liveThunks';
import type { ChatMessageDto, PostChatMessageInput } from '@/features/live/types';
import {
  clearPublicOffer,
  clearPublicQuiz,
  clearPublicResource,
  clearPublicSurvey,
  closeOfferEmbed,
  hidePublicOffer,
  hidePublicQuiz,
  hidePublicSurvey,
  openOfferEmbed,
} from '@/features/live/liveSlice';
import { fetchMyPublicSurveySubmission } from '@/features/webinar/webinarThunks';
import { ablyPrivateChatChannelName, type LiveChatMode } from '@/features/live/ablySessionChannels';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import type { Message } from 'ably';
import { useAbly, useChannel } from 'ably/react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import {
  Check,
  Crown,
  ChevronDown,
  icons as lucideIcons,
  MessageCircle,
  MessageCircleQuestion,
  Pin,
  Sword,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import BlockIcon from '@/components/icons/BlockIcon';
import PinIcon from '@/components/icons/PinIcon';
import ReadIcon from '@/components/icons/ReadIcon';
import OfferCard from '@/components/common/asset/OfferCard';
import SurveyCard from '@/components/common/asset/SurveyCard';
import QuizCard from '@/components/common/asset/QuizCard';
import SharedResourceViewer from '@/components/pages/live/attendee/SharedResourceViewer';
import PopupBuyerCard from '@/components/common/asset/PopupBuyerCard';
import { toast } from 'react-toastify';
import { getSubscriptionAttendeeChatIdentity } from '@/lib/publicLiveJwt';
import { chatViewerFromProps, isModeratedChatMessageVisible } from '@/lib/chatModeration';
import { isAttendeeChatWriteLocked, isChatDisabledMode } from '@/lib/chatWriteAccess';
import { buildChatReactionAnalyticsPayload, mergeReactionsByMessage } from '@/lib/chatReactions';
import { isOrphanPrivateReply } from '@/lib/chatReplyContext';
import {
  QUESTION_HIGHLIGHT_BACKGROUND,
  shouldHighlightQuestion,
} from '@/lib/chatQuestionHighlight';
import { canMoveMessageToQuestions, canResolveQuestion } from '@/lib/chatQuestionEligibility';
import { resolveReplyErrorKey } from '@/lib/chatReplyError';
import { toPlaybackAt } from '@/lib/playbackAt';
import { buildChatCapKeepAll, capChatMessages } from '@/lib/chatListCap';
import {
  resolveScrollCorrection,
  selectSurvivingAnchor,
  type ScrollAnchorCandidate,
} from '@/lib/chatScrollAnchor';
import {
  historyContainsMessageId,
  recordPinnedHydrationAttempt,
  selectPinnedIdsFromHistory,
  shouldHydratePinnedMessage,
  waitBeforePinnedHydrationRetry,
  type PinMutation,
} from '@/lib/pinnedMessageHydration';
import {
  extractPurchaseNotificationOfferColors,
  resolvePurchaseNotificationDisplay,
} from '@/lib/purchaseNotificationDisplay';
import { createLiveEventDeduplicator, readLiveEventId } from '@/lib/liveEventBatch';
import { useWebinarT } from '@/hooks/useWebinarT';
import OfferEmbedPanel from '@/components/pages/live/attendee/OfferEmbedPanel';
import { shouldShowOfferEmbed } from '@/lib/offerEmbed';

type MessageReactions = Record<string, number>;

function readSessionStorageFlag(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeSessionStorageFlag(key: string): void {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // ignore
  }
}

function getChatHistoryJitterStorageKey(webinarId: number, sessionIdentifier: string): string {
  return `welya-chat-history-jitter-done-${webinarId}-${sessionIdentifier}`;
}

export function clearChatHistoryJitterFlag(webinarId: number, sessionIdentifier: string): void {
  try {
    window.sessionStorage.removeItem(getChatHistoryJitterStorageKey(webinarId, sessionIdentifier));
  } catch {
    // ignore
  }
}

function iconNameToPascal(name: string): string {
  return name
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');
}
function linkifyChatMessageText(
  text: string | undefined,
  vi?: WorkspaceVisualIdentity,
  onLinkClick?: (url: string) => void,
): React.ReactNode {
  if (text == null || text === '') return text ?? '';
  const spans: { s: number; e: number; r: string }[] = [];
  for (const re of [
    /https?:\/\/[^\s<>"']+/gi,
    /www\.[^\s<>"']+/gi,
    /(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(?:\/[^\s<>"']*)?/gi,
  ]) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      if (text[m.index - 1] === '@') continue;
      spans.push({ s: m.index, e: m.index + m[0].length, r: m[0] });
    }
  }
  spans.sort((a, b) => b.e - b.s - (a.e - a.s));
  const sel: typeof spans = [];
  for (const x of spans) if (!sel.some((p) => x.s < p.e && x.e > p.s)) sel.push(x);
  sel.sort((a, b) => a.s - b.s);
  if (!sel.length) return text;
  const cls = cn(
    'underline hover:opacity-80 wrap-break-word',
    vi?.theme === 'DARK' ? 'text-white' : 'text-black',
  );
  const out: React.ReactNode[] = [];
  let last = 0;
  for (let i = 0; i < sel.length; i++) {
    const { s, e, r } = sel[i];
    if (s > last) out.push(text.slice(last, s));
    const t = r.trim();
    const href = /^https?:\/\//i.test(t) ? t : `https://${t}`;
    out.push(
      <a
        key={i}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cls}
        onClick={() => onLinkClick?.(href)}
      >
        {r}
      </a>,
    );
    last = e;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function getMessageIdFromData(data: any): string | number | undefined | null {
  if (data == null) return undefined;
  if (data.id != null) return data.id;
  if (data.messageId != null) return data.messageId;
  return undefined;
}

/** Clé stable pour éviter les doublons (historique + Ably, ou canal principal + staff). */
function getMessageDedupKey(msg: Message): string {
  const dataId = getMessageIdFromData(msg.data);
  if (dataId != null) return `id:${dataId}`;

  const d = msg.data as Record<string, unknown> | null | undefined;
  const text = (
    typeof d?.text === 'string' ? d.text : typeof d?.content === 'string' ? d.content : ''
  ).trim();
  const userId = d?.userId != null ? String(d.userId) : '';
  const ts = msg.timestamp ?? 0;
  if (text) return `content:${userId}:${text}:${Math.floor(ts / 1000)}`;

  return `ably:${msg.id ?? ts}`;
}

function dedupeMessagesByKey(messages: Message[]): Message[] {
  const seen = new Set<string>();
  const out: Message[] = [];
  for (const msg of messages) {
    const key = getMessageDedupKey(msg);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(msg);
  }
  return out;
}

function getMessageTimestamp(msg: Message): number {
  if (typeof msg.timestamp === 'number' && Number.isFinite(msg.timestamp)) {
    return msg.timestamp;
  }
  const d = msg.data as Record<string, unknown> | null | undefined;
  if (typeof d?.createdAt === 'string') {
    const parsed = Date.parse(d.createdAt);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

const CHAT_MESSAGE_MAX_LENGTH = 300;

/** Nombre de messages retenus comme repères de scroll, du premier visible vers le bas. */
const ANCHOR_CANDIDATE_COUNT = 8;

function clampChatMessageContent(content: string): string {
  return content.slice(0, CHAT_MESSAGE_MAX_LENGTH);
}

function sortMessagesChronologically(messages: Message[]): Message[] {
  return [...messages].sort((a, b) => getMessageTimestamp(a) - getMessageTimestamp(b));
}

/**
 * `keepPendingQuestions` (vues staff) : une question encore ouverte survit au plafond, sinon elle
 * disparaîtrait de la seule vue où le staff peut la traiter dès que le live dépasse 300 messages.
 * `pinnedIds` : un message épinglé survit aussi, sinon le bloc épinglé se vide de lui-même dès que
 * le live dépasse 300 messages (les épinglés du chargement initial sont les plus anciens).
 */
function dedupeAndSortMessages(
  messages: Message[],
  keepPendingQuestions = false,
  pinnedIds?: ReadonlySet<string>,
): Message[] {
  const deduped = sortMessagesChronologically(dedupeMessagesByKey(messages));
  return capChatMessages(deduped, {
    keepAll: buildChatCapKeepAll<Message>({
      keepPendingQuestions,
      pinnedIds,
      getMessageId: (msg) => getMessageIdFromData(msg.data),
    }),
  });
}

/** Fusionne sans doublon : les plus récents restent en bas du chat. */
function mergeMessagesDeduped(
  existing: Message[],
  incoming: Message[],
  keepPendingQuestions = false,
  pinnedIds?: ReadonlySet<string>,
): Message[] {
  return dedupeAndSortMessages([...existing, ...incoming], keepPendingQuestions, pinnedIds);
}

const CHAT_QUESTION_STATE_EVENT = 'CHAT_QUESTION_STATE_CHANGED';
const CHAT_QUESTION_ACTION_MOVED = 'MOVED_TO_QUESTIONS';
const CHAT_QUESTION_ACTION_HANDLED = 'HANDLED';

function getSubscriptionIdFromData(data: any): number | undefined {
  if (data == null || typeof data !== 'object') return undefined;
  const id = data.subscriptionId;
  return typeof id === 'number' && Number.isFinite(id) ? id : undefined;
}

function mapDtoReactionsToMessageReactions(
  reactions: ChatMessageDto['reactions'],
): Record<string, number> {
  const out: Record<string, number> = {};
  if (!reactions?.length) return out;
  for (const r of reactions) {
    out[r.emoji] = r.count;
  }
  return out;
}

function coerceBoolFromUnknown(v: unknown): boolean | undefined {
  if (v === true || v === 'true') return true;
  if (v === false || v === 'false') return false;
  return undefined;
}

function coerceFiniteNumberFromUnknown(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return undefined;
}
function normalizeAblyChatMessagePayload(raw: unknown): unknown {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const s = raw as Record<string, unknown>;
  const out: Record<string, unknown> = { ...s };

  const rid = coerceFiniteNumberFromUnknown(s.recipientAttendeeId ?? s.recipient_attendee_id);
  if (rid != null && out.recipientAttendeeId == null) out.recipientAttendeeId = rid;

  const replyTo = coerceFiniteNumberFromUnknown(s.repliedToMessageId ?? s.replied_to_message_id);
  if (replyTo != null && out.repliedToMessageId == null) out.repliedToMessageId = replyTo;

  const pm =
    coerceBoolFromUnknown(s.privateMessage) ??
    coerceBoolFromUnknown(s.private_message) ??
    coerceBoolFromUnknown(s.private);
  if (pm === true) out.privateMessage = true;

  const tm = coerceBoolFromUnknown(s.teamMember) ?? coerceBoolFromUnknown(s.team_member);
  if (tm === true) out.teamMember = true;

  const qh = coerceBoolFromUnknown(s.questionHandled) ?? coerceBoolFromUnknown(s.question_handled);
  if (qh != null) out.questionHandled = qh;

  const uid =
    coerceFiniteNumberFromUnknown(s.userId) ??
    coerceFiniteNumberFromUnknown(s.user_id) ??
    coerceFiniteNumberFromUnknown(s.attendeeId) ??
    coerceFiniteNumberFromUnknown(s.attendee_id);
  if (uid != null && out.userId == null) out.userId = uid;

  const msgId =
    coerceFiniteNumberFromUnknown(s.id) ??
    coerceFiniteNumberFromUnknown(s.messageId ?? s.message_id);
  if (msgId != null) {
    if (out.id == null) out.id = msgId;
    if (out.messageId == null) out.messageId = msgId;
  }

  if (typeof s.content === 'string' && out.content == null) out.content = s.content;
  if (typeof s.text === 'string' && out.text == null) out.text = s.text;

  return out;
}

function isExplicitPrivateChatPayload(data: unknown): boolean {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return false;
  const d = data as Record<string, unknown>;
  if (d.privateMessage === true || d.private === true || d.private_message === true) return true;
  if (d.recipientAttendeeId != null || d.recipient_attendee_id != null) return true;
  return false;
}

function isPrivateChatMessageVisibleToAttendee(
  msg: Message,
  currentUserId: string,
  findMessageById: (messageId: string | number) => Message | undefined,
): boolean {
  const d: any = msg.data;
  if (!d || typeof d !== 'object') return false;
  const me = String(currentUserId);
  if (!me) return false;

  if (d.privateMessage === true && d.recipientAttendeeId != null) {
    return String(d.recipientAttendeeId) === me;
  }

  if (d.teamMember === true) {
    const replyId = d.repliedToMessageId;
    if (replyId == null) {
      return true;
    }
    const parent = findMessageById(replyId);
    if (!parent) {
      if (d.privateMessage === true && d.recipientAttendeeId != null) {
        return String(d.recipientAttendeeId) === me;
      }
      return false;
    }
    return isPrivateChatMessageVisibleToAttendee(parent, currentUserId, findMessageById);
  }

  const uid = d.userId != null ? String(d.userId) : '';
  return uid === me;
}

function isEvergreenAttendeeVisibleMessage(msg: Message, currentUserId: string): boolean {
  const data = msg.data as {
    userId?: unknown;
    attendeeId?: unknown;
    evergreen?: unknown;
    teamMember?: unknown;
  } | null;
  if (!data || typeof data !== 'object') return false;
  if (data.evergreen === true) return true;
  if (data.teamMember === true) return true;
  if (!currentUserId) return false;
  const msgUserId =
    data.userId != null
      ? String(data.userId)
      : data.attendeeId != null
        ? String(data.attendeeId)
        : '';
  return msgUserId === String(currentUserId);
}

function chatMessageDtoToMessage(dto: ChatMessageDto): Message {
  const reactions = mapDtoReactionsToMessageReactions(dto.reactions);
  return {
    id: String(dto.id),
    data: {
      id: dto.id,
      messageId: dto.id,
      text: dto.content,
      content: dto.content,
      userName: dto.authorDisplayName,
      userId: dto.attendeeId,
      teamMember: dto.teamMember,
      subscriptionId: dto.subscriptionId,
      banned: dto.banned,
      repliedToMessageId: dto.repliedToMessageId,
      privateMessage: dto.privateMessage === true,
      recipientAttendeeId: dto.recipientAttendeeId,
      statusLabel: dto.statusLabel,
      statusColor: dto.statusColor,
      statusIcon: dto.statusIcon,
      role: dto.role,
      dirty: dto.dirty === true,
      evergreen: dto.evergreen === true,
      question: dto.question === true,
      questionHandled: dto.questionHandled === true,
      reactions,
    },
    timestamp: new Date(dto.createdAt).getTime(),
  } as Message;
}

function getRepliedToMessageIdFromData(data: unknown): number | null {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return null;
  const d = data as Record<string, unknown>;
  const replyToId = coerceFiniteNumberFromUnknown(d.repliedToMessageId ?? d.replied_to_message_id);
  return replyToId ?? null;
}

function isDirectReplyToMessage(msg: Message, parentMessageId: string): boolean {
  const replyToId = getRepliedToMessageIdFromData(msg.data);
  return replyToId != null && replyToId.toString() === parentMessageId;
}

function patchMessageQuestionHandledState(msg: Message): Message {
  if (msg.data == null || typeof msg.data !== 'object' || Array.isArray(msg.data)) return msg;
  return {
    ...msg,
    data: {
      ...(msg.data as Record<string, unknown>),
      question: false,
      questionHandled: true,
    },
  };
}

function patchMessageActiveQuestionState(msg: Message): Message {
  if (msg.data == null || typeof msg.data !== 'object' || Array.isArray(msg.data)) return msg;
  return {
    ...msg,
    data: {
      ...(msg.data as Record<string, unknown>),
      question: true,
      questionHandled: false,
    },
  };
}

/** Bulle de chat avec une coche : action « Résoudre » (lucide n'a pas ce glyphe en 0.548). */
function BubbleCheckIcon({
  size = 24,
  color = 'white',
  className,
}: {
  size?: number;
  color?: string;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="m8.5 10 2.2 2.2 4.8-4.8" />
    </svg>
  );
}

export function LucideLazyIcon({
  name,
  ...rest
}: { name: string } & React.SVGProps<SVGSVGElement>) {
  if (!name || typeof name !== 'string') return null;
  const pascalName = iconNameToPascal(name);
  const IconComponent = (
    lucideIcons as Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>>
  )[pascalName];
  if (!IconComponent) return null;
  return <IconComponent {...rest} />;
}

export function MessageView({
  message,
  isAdmin,
  isAttendee,
  currentUserId,
  reactions,
  onReact,
  onPinMessage,
  isPinned,
  onDeleteMessage,
  onStartReply,
  onPrivateStartReply,
  isReplyTarget,
  isPrivateReply,
  replyText,
  onChangeReplyText,
  onCancelReply,
  onSendReply,
  repliedToMessage,
  onBlockUser,
  blockingMessageId,
  onConfirmBlock,
  onCancelBlock,
  visualIdentity,
  isSending = false,
  isReactionDisabled = false,
  onMoveToQuestions,
  onValidateQuestion,
  onOfferLinkClick,
}: {
  message: Message;
  isAdmin: boolean;
  isAttendee: boolean;
  currentUserId?: string;
  reactions?: MessageReactions;
  onReact: (messageId: number | undefined, emoji: string) => void;
  onPinMessage: (messageId: number | undefined) => Promise<void>;
  isPinned?: boolean;
  onDeleteMessage: (messageId: number | undefined) => void;
  onStartReply?: (messageId?: string | number | null) => void;
  onPrivateStartReply?: (messageId?: string | number | null) => void;
  isReplyTarget?: boolean;
  isPrivateReply?: boolean;
  replyText?: string;
  onChangeReplyText?: (value: string) => void;
  onCancelReply?: () => void;
  onSendReply?: () => void;
  repliedToMessage?: Message;
  onBlockUser?: (messageId: number | undefined) => void;
  blockingMessageId?: number | null;
  onConfirmBlock?: (subscriptionId: number | undefined) => void;
  onCancelBlock?: () => void;
  visualIdentity: WorkspaceVisualIdentity;
  isSending?: boolean;
  isReactionDisabled?: boolean;
  onMoveToQuestions?: (messageId: number | undefined) => void;
  onValidateQuestion?: (messageId: number | undefined) => void;
  onOfferLinkClick?: (url: string) => void;
}) {
  const { t } = useWebinarT('webinars');
  const tChatMsg = (key: string, opts?: Record<string, unknown>) =>
    isAttendee ? t(`live.chat.${key}`, opts) : t(`adminLive.chat.${key}`, opts);
  const [hoveredMessageId, setHoveredMessageId] = useState<number | null>(null);

  const reactionEmojis = ['🔥', '🚀​', '👍', '😍', '🤣​'];

  let displayName: string | undefined;
  let content: string = '';
  let authorNameFromData: string | undefined;
  let userIdFromData: string | undefined;
  let isReaction: boolean = false;
  let teamMemberFromData: boolean = false;
  let bannedFromData: boolean = false;
  let isModeratorFromData: boolean = false;
  let isAdminFromData: boolean = false;
  let statusLabel: string | undefined;
  let statusColor: string | undefined;
  let statusIcon: string | undefined;
  let evergreenFromData = false;
  let questionFromData = false;
  let questionHandledFromData = false;
  let privateMessageFromData = false;
  let recipientAttendeeIdFromData: number | null = null;
  let repliedToMessageIdFromData: number | null = null;

  if (typeof message.data === 'string') {
    content = message.data;
  } else if (message.data && typeof message.data === 'object') {
    const data: any = message.data;
    content = data.text ?? data.content ?? '';
    isReaction = data.reaction === true;
    if (typeof data.userName === 'string') {
      authorNameFromData = data.userName;
    }
    if (data.userId != null) {
      userIdFromData = String(data.userId);
    }
    if (data.teamMember === true) {
      teamMemberFromData = true;
    }
    if (data.role === 'MODERATOR') {
      isModeratorFromData = true;
    }
    if (data.role === 'ADMIN') {
      isAdminFromData = true;
    }
    if (data.role === 'PRESENTER') {
      isAdminFromData = true;
    }
    if (data.banned === true) {
      bannedFromData = true;
    }
    if (data.statusLabel != null || data.statusColor != null || data.statusIcon != null) {
      statusLabel = data.statusLabel || undefined;
      statusColor = data.statusColor || undefined;
      statusIcon = data.statusIcon || undefined;
    }
    if (data.evergreen === true) {
      evergreenFromData = true;
    }
    if (data.question === true) {
      questionFromData = true;
    }
    if (data.questionHandled === true) {
      questionHandledFromData = true;
    }
    if (data.privateMessage === true) {
      privateMessageFromData = true;
    }
    if (data.recipientAttendeeId != null) {
      const recipientId = Number(data.recipientAttendeeId);
      if (!Number.isNaN(recipientId)) {
        recipientAttendeeIdFromData = recipientId;
      }
    }
    if (data.repliedToMessageId != null) {
      const replyToId = Number(data.repliedToMessageId);
      if (!Number.isNaN(replyToId)) {
        repliedToMessageIdFromData = replyToId;
      }
    }
  }

  const isTeamMemberOnly = teamMemberFromData && !isModeratorFromData;

  // Rôle Présentateur/Modérateur : visuel piloté par la config (statut/couleur/icône) avec repli
  // sur l'ancien rendu codé en dur (Crown jaune / Sword vert) si aucune config n'est remontée.
  // En temps réel le message porte data.role ; dans l'historique (reload) le rôle n'est pas renvoyé,
  // on retombe alors sur teamMember + statut configuré (seuls présentateur/modérateur en ont un).
  const hasConfiguredStatus = Boolean(statusLabel || statusColor || statusIcon);
  const isRoleMessage =
    isAdminFromData || isModeratorFromData || (teamMemberFromData && hasConfiguredStatus);
  const roleFallbackTextClass = isAdminFromData ? 'text-yellow-500' : 'text-green-500';
  const roleColorStyle = isRoleMessage && statusColor ? { color: statusColor } : undefined;

  let repliedToContent: string | undefined;
  let repliedToAuthor: string | undefined;

  if (repliedToMessage) {
    const repliedData: any = repliedToMessage.data;

    if (typeof repliedData === 'string') {
      repliedToContent = repliedData;
    } else if (repliedData && typeof repliedData === 'object') {
      repliedToContent = repliedData.text ?? repliedData.content ?? '';
      if (typeof repliedData.userName === 'string') {
        repliedToAuthor = repliedData.userName;
      }
    }
  }

  const orphanPrivateReply = isOrphanPrivateReply({
    isStaffView: isAdmin,
    privateMessage: privateMessageFromData,
    teamMember: teamMemberFromData,
    repliedToMessageId: repliedToMessageIdFromData,
    hasParentLoaded: Boolean(repliedToMessage),
  });

  if (authorNameFromData) {
    displayName = authorNameFromData.trim();
  }

  const messageId = getMessageIdFromData(message.data);
  const subscriptionId = getSubscriptionIdFromData(message.data);
  const isBlocking =
    messageId != null &&
    blockingMessageId != null &&
    Number(messageId) === Number(blockingMessageId);
  const isBannedDisplay =
    bannedFromData &&
    isAdmin &&
    currentUserId != null &&
    userIdFromData != null &&
    String(currentUserId) !== String(userIdFromData);

  const isBaseMessage = !isBlocking && !isBannedDisplay && !isPinned;
  const baseBackgroundColor = visualIdentity?.backgroundColor || '#FFFFFF';
  const shouldUseBaseBackground = isBaseMessage || isPinned;
  const evergreenMuted =
    isAdmin && evergreenFromData && !isBlocking && !isBannedDisplay && !isReaction;
  // Question traitée : grisée + taguée « Répondue ». Concept staff uniquement : côté spectateur le
  // message reste normal.
  const isHandledQuestion =
    isAdmin && questionHandledFromData && !isBlocking && !isBannedDisplay && !isReaction;
  // Question en attente : fond jaune, staff uniquement.
  const isHighlightedQuestion = shouldHighlightQuestion({
    isStaffView: isAdmin,
    question: questionFromData,
    questionHandled: questionHandledFromData,
    reaction: isReaction,
    blocking: isBlocking,
    banned: isBannedDisplay,
  });
  // Le jaune est un aplat clair : le texte reste sombre même en thème sombre.
  const bodyTextClass = isHighlightedQuestion
    ? 'text-black'
    : visualIdentity?.theme === 'DARK'
      ? 'text-white'
      : 'text-black';
  const questionEligibility = {
    question: questionFromData,
    questionHandled: questionHandledFromData,
    reaction: isReaction,
    staffAuthor: teamMemberFromData || isModeratorFromData || isAdminFromData,
    privateMessage: privateMessageFromData,
    recipientAttendeeId: recipientAttendeeIdFromData,
    repliedToMessageId: repliedToMessageIdFromData,
  };
  const canMoveToQuestions = canMoveMessageToQuestions(questionEligibility);
  const canValidateQuestion = canResolveQuestion(questionEligibility);

  return (
    <div
      className={`flex flex-col relative p-2 mx-2 rounded-t-lg rounded-br-lg ${isRoleMessage ? (statusColor ? 'border' : isAdminFromData ? 'border border-yellow-500' : 'border border-green-500') : ''} ${
        isBlocking
          ? 'bg-[#8D1100] border '
          : isBannedDisplay
            ? 'bg-[#8D1100] border '
            : isPinned
              ? 'mx-1! rounded! shadow-xl'
              : ''
      } ${evergreenMuted || isHandledQuestion ? 'opacity-50' : ''}`}
      style={
        shouldUseBaseBackground
          ? {
              backgroundColor: isHighlightedQuestion
                ? QUESTION_HIGHLIGHT_BACKGROUND
                : baseBackgroundColor,
              ...(isRoleMessage && statusColor && !isPinned && { borderColor: statusColor }),
              ...(isPinned && {
                borderColor: isRoleMessage
                  ? statusColor || (isAdminFromData ? '#FFD700' : 'oklch(72.3% 0.219 149.579)')
                  : visualIdentity?.contrastColor || '#2C6CFF',
              }),
            }
          : undefined
      }
      onMouseEnter={() =>
        !isReaction && setHoveredMessageId(messageId != null ? Number(messageId) : null)
      }
      onMouseLeave={() => setHoveredMessageId(null)}
    >
      {/* MODO ADMIN ou MODERATEUR */}
      {!isReaction &&
        isAdmin &&
        hoveredMessageId === (messageId != null ? Number(messageId) : null) && (
          <div className="absolute z-10 shadow-md translate-y-[-50%] top-0 right-0 rounded-full p-1 bg-[#EAF0FF] flex items-center justify-center gap-2 transition-opacity duration-200">
            <div
              className="rounded-full bg-[#2EFFB8] cursor-pointer hover:bg-green-500 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onStartReply?.(messageId != null ? Number(messageId) : undefined);
              }}
              title={t('adminLive.chat.reply')}
            >
              <ReadIcon size={24} className="p-1" />
            </div>
            <div
              className="rounded-full bg-orange cursor-pointer hover:bg-orange/80 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onPrivateStartReply?.(messageId != null ? Number(messageId) : undefined);
              }}
              title={t('adminLive.chat.privateReply')}
            >
              <img
                src="/assets/privateMessage.svg"
                alt={t('adminLive.chat.privateReply')}
                className="w-6 h-6 p-1"
              />
            </div>
            {canValidateQuestion && (
              <div className="relative group/resolve">
                <div
                  className="rounded-full bg-[#22C55E] cursor-pointer hover:bg-green-600 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onValidateQuestion?.(messageId != null ? Number(messageId) : undefined);
                  }}
                >
                  <BubbleCheckIcon size={24} color="white" className="p-1" />
                </div>
                <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-white px-3 py-1 text-sm font-medium text-secondary shadow-md opacity-0 transition-opacity duration-150 group-hover/resolve:opacity-100">
                  {t('adminLive.chat.resolve')}
                </span>
              </div>
            )}
            {canMoveToQuestions && (
              <div
                className="rounded-full bg-[#FFC700] cursor-pointer hover:bg-yellow-500 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onMoveToQuestions?.(messageId != null ? Number(messageId) : undefined);
                }}
                title={t('adminLive.chat.moveToQuestions')}
              >
                <MessageCircleQuestion size={24} color="white" className="p-1" />
              </div>
            )}
            <div
              className={`rounded-full cursor-pointer transition-colors ${
                isPinned ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-[#2C6CFF] hover:bg-blue-600'
              }`}
              onClick={() => onPinMessage(messageId != null ? Number(messageId) : undefined)}
              title={isPinned ? t('adminLive.chat.unpin') : t('adminLive.chat.pin')}
            >
              <PinIcon size={24} color="white" className="p-1" />
            </div>
            <div
              className="size-6 rounded-full bg-white cursor-pointer flex items-center justify-center hover:bg-red-50 transition-colors"
              onClick={() => {
                onDeleteMessage(messageId != null ? Number(messageId) : undefined);
              }}
              title={t('adminLive.chat.delete')}
            >
              <Trash2 size={24} color="#6D6E7A" className="p-1" />
            </div>
            <div
              className="size-6 rounded-full bg-[#FAE6E3] cursor-pointer hover:bg-red-100 transition-colors"
              onClick={() => onBlockUser?.(messageId != null ? Number(messageId) : undefined)}
              title={t('adminLive.chat.blockUser')}
            >
              <BlockIcon size={24} color="#F1361D" className="p-1" />
            </div>
          </div>
        )}

      {!isReaction &&
        isAttendee &&
        hoveredMessageId === (messageId != null ? Number(messageId) : null) && (
          <div
            className="absolute z-10 shadow-md translate-y-[-50%] top-0 right-0 rounded-full p-1 flex items-center justify-center gap-2 transition-opacity duration-200"
            style={{
              backgroundColor: visualIdentity?.backgroundColor || '#EAF0FF',
            }}
          >
            {reactionEmojis.map((emoji) => (
              <button
                key={emoji}
                type="button"
                disabled={isSending || isReactionDisabled}
                className="rounded-full cursor-pointer transition-colors w-7 h-7 flex items-center justify-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor:
                    visualIdentity?.theme === 'LIGHT'
                      ? 'rgba(31, 31, 31, 0.08)'
                      : 'rgba(255, 255, 255, 0.2)',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isSending && !isReactionDisabled)
                    onReact(messageId != null ? Number(messageId) : undefined, emoji);
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

      <div className="flex items-start flex-col justify-start gap-1">
        {isPinned && (
          <div
            className={cn(
              'flex items-center gap-1 text-xs font-normal',
              isHighlightedQuestion ? 'text-black' : 'text-white',
            )}
          >
            <Pin size={14} /> <span>{tChatMsg('pinned')}</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          {isRoleMessage ? (
            statusIcon ? (
              <LucideLazyIcon
                name={statusIcon}
                width={14}
                height={14}
                color={statusColor || (isAdminFromData ? '#EAB308' : '#22C55E')}
              />
            ) : isAdminFromData ? (
              <Crown
                size={14}
                className={statusColor ? undefined : 'text-yellow-500'}
                style={statusColor ? { color: statusColor } : undefined}
                strokeWidth={3}
              />
            ) : isModeratorFromData ? (
              <Sword
                size={14}
                className={statusColor ? undefined : 'text-green-500'}
                style={statusColor ? { color: statusColor } : undefined}
                strokeWidth={3}
              />
            ) : null
          ) : (
            statusIcon && (
              <LucideLazyIcon
                name={statusIcon}
                width={14}
                height={14}
                color={statusColor || '#2C6CFF'}
              />
            )
          )}
          <span
            className={`text-xs ${
              isHighlightedQuestion
                ? `${isRoleMessage ? 'font-bold' : 'font-normal'} text-black`
                : isRoleMessage
                  ? `font-bold ${statusColor ? '' : roleFallbackTextClass}`
                  : `font-normal ${visualIdentity?.theme === 'LIGHT' ? 'text-secondary' : 'text-white'}`
            }`}
            style={
              // Sur l'aplat jaune, la couleur de statut du participant est ignorée : un statut
              // clair rendait le nom illisible, voire invisible quand il est blanc.
              isHighlightedQuestion
                ? undefined
                : (roleColorStyle ??
                  (!isRoleMessage && !isTeamMemberOnly && statusColor
                    ? { color: statusColor }
                    : undefined))
            }
          >
            {displayName || ''}
            {statusLabel && (
              <span
                className={
                  isHighlightedQuestion
                    ? `${isRoleMessage ? 'font-bold ' : ''}text-black`
                    : isRoleMessage
                      ? `font-bold ${statusColor ? '' : roleFallbackTextClass}`
                      : 'text-tertiary'
                }
                style={isHighlightedQuestion ? undefined : roleColorStyle}
              >
                {' '}
                - {statusLabel}
              </span>
            )}
          </span>
          {isHandledQuestion && (
            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
              <Check size={10} strokeWidth={3} /> {t('adminLive.chat.answered')}
            </span>
          )}
        </div>
      </div>
      {reactions && Object.keys(reactions).length > 0 && (
        <div className="absolute top-1 right-1">
          <div className="flex items-center gap-2">
            {Object.entries(reactions).map(([emoji, count]) =>
              count > 0 ? (
                <span
                  key={emoji}
                  className="rounded-full py-0.5 px-1.5 text-xs"
                  style={{
                    color: visualIdentity?.theme === 'LIGHT' ? '#000000' : '#FFFFFF',
                    backgroundColor:
                      visualIdentity?.theme === 'LIGHT'
                        ? 'rgba(31, 31, 31, 0.03)'
                        : 'rgba(255, 255, 255, 0.1)',
                  }}
                >
                  {emoji} {count}
                </span>
              ) : null,
            )}
          </div>
        </div>
      )}

      <div className="text-xs sm:text-sm md:text-base">
        {isBlocking ? (
          <div className="flex flex-col gap-2">
            <p className="text-white">
              {bannedFromData
                ? t('adminLive.chat.unblockConfirmQuestion')
                : t('adminLive.chat.blockConfirmQuestion')}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => onCancelBlock?.()}
                className="text-accent cursor-pointer text-sm"
              >
                {t('adminLive.chat.cancel')}
              </button>
              <button
                type="button"
                onClick={() => onConfirmBlock?.(subscriptionId)}
                className="px-3 py-1 text-sm bg-[#E94B36] text-white rounded hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {bannedFromData
                  ? t('adminLive.chat.confirmUnblock')
                  : t('adminLive.chat.confirmBlock')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {((repliedToMessage && repliedToContent) || orphanPrivateReply) && (
              <div
                className="border-l-4 rounded-r-md rounded-l-sm py-1 my-0.5"
                style={{
                  borderColor: visualIdentity?.contrastColor || '#2C6CFF',
                  backgroundColor:
                    isHighlightedQuestion || visualIdentity?.theme === 'LIGHT'
                      ? 'rgba(31, 31, 31, 0.08)'
                      : 'rgba(255, 255, 255, 0.2)',
                }}
              >
                <div className="pl-1">
                  <p
                    className="text-[10px] font-semibold"
                    style={{
                      color: visualIdentity?.contrastColor || '#2C6CFF',
                    }}
                  >
                    {orphanPrivateReply
                      ? t('live.chat.yourPreviousMessage')
                      : repliedToAuthor || t('live.chat.previousMessage')}
                  </p>
                  {repliedToContent && (
                    <p className={cn('text-xs font-normal', bodyTextClass)}>{repliedToContent}</p>
                  )}
                </div>
              </div>
            )}
            <p style={{ wordBreak: 'break-word' }} className={cn('wrap-break-word', bodyTextClass)}>
              {isModeratorFromData || teamMemberFromData || isAdminFromData
                ? linkifyChatMessageText(
                    content,
                    visualIdentity,
                    isAttendee ? onOfferLinkClick : undefined,
                  )
                : (content ?? '')}
            </p>
          </>
        )}
      </div>

      {/* Zone de réponse intégrée */}
      {isReplyTarget && (
        <div
          className="mt-3 p-3 bg-gray-50 rounded-lg border"
          style={{
            backgroundColor: baseBackgroundColor,
          }}
        >
          <div className="flex flex-col gap-2">
            {isPrivateReply && (
              <p
                className={`text-xs font-medium ${visualIdentity?.theme === 'DARK' ? 'text-orange-300' : 'text-orange-700'}`}
              >
                {tChatMsg('privateReplyLabel')}
              </p>
            )}
            <textarea
              value={replyText ?? ''}
              onChange={(e) => onChangeReplyText?.(clampChatMessageContent(e.target.value))}
              maxLength={CHAT_MESSAGE_MAX_LENGTH}
              placeholder={tChatMsg('typeReply')}
              className={cn(
                'w-full h-20 p-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:border-(--focus-color) placeholder-gray-500',
                visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black',
              )}
              style={
                {
                  '--focus-color': visualIdentity?.contrastColor || '#2C6CFF',
                } as React.CSSProperties
              }
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => onCancelReply?.()}
                className="cursor-pointer text-sm"
                style={{
                  color: visualIdentity?.contrastColor || '#2C6CFF',
                }}
              >
                {tChatMsg('cancel')}
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  if (!isSending) onSendReply?.();
                }}
                disabled={!replyText?.trim() || isSending}
                className={cn(
                  'px-3 py-1 text-sm  rounded disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors',
                )}
                style={{
                  backgroundColor: visualIdentity?.contrastColor || '#F3F3F4',
                }}
              >
                {tChatMsg('sendReply')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ChatView({
  workspaceId,
  webinarId,
  sessionId,
  channelName,
  userId,
  isAdmin,
  isSpeaker,
  isAttendee,
  attendeeSessionIdentifier,
  attendeeAuthorization,
  speakerAuthorization,
  onResourceEvent,
  onSurveyResultsEvent,
  visualIdentity,
  chatMode,
  liveParticipantCountSync,
  isEvergreen,
  getPlaybackTimestamp,
}: {
  workspaceId?: number;
  webinarId?: number;
  sessionId?: number;
  channelName: string;
  userId: string;
  userName?: string;
  isAdmin?: boolean;
  isSpeaker?: boolean;
  isAttendee?: boolean;
  attendeeSessionIdentifier?: string;
  attendeeAuthorization?: string;
  speakerAuthorization?: string;
  onResourceEvent?: (event: {
    eventType: 'RESOURCE_SHOW' | 'RESOURCE_HIDE';
    resourceType: string;
    resourceId: number;
    timeNow: number;
  }) => void;
  onSurveyResultsEvent?: (event: {
    eventType: 'SHOW_SURVEY_RESPONSE' | 'HIDE_SURVEY_RESPONSE';
    templateId: number;
    timeNow: number;
    onUpdate: (templateId: number, show: boolean) => void;
  }) => void;
  visualIdentity?: WorkspaceVisualIdentity;
  chatMode?: LiveChatMode;
  isEvergreen?: boolean;
  liveParticipantCountSync?: number;
  getPlaybackTimestamp?: () => number | null;
}) {
  // Vues staff : les questions ouvertes ne tombent pas sous le plafond du flux courant.
  const keepPendingQuestions = Boolean(isAdmin);
  const { t } = useWebinarT('webinars');
  const tChat = (key: string, opts?: Record<string, unknown>) =>
    isAttendee ? t(`live.chat.${key}`, opts) : t(`adminLive.chat.${key}`, opts);
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [allMessages, setAllMessages] = useState<Message[]>([]);
  const [reactionsByMessage, setReactionsByMessage] = useState<Record<string, MessageReactions>>(
    {},
  );
  const [pinnedMessages, setPinnedMessages] = useState<Set<string>>(new Set());
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [isPrivateReply, setIsPrivateReply] = useState(false);
  /** Lu au moment de l’envoi (évite toute désynchro d’état ; synchrone avec les clics privé / public). */
  const isPrivateReplyRef = useRef(false);
  const [replyText, setReplyText] = useState<string>('');
  const [blockingMessageId, setBlockingMessageId] = useState<number | null>(null);
  const [surveysShowingResults, setSurveysShowingResults] = useState<Set<number>>(new Set());
  const [reactionCooldownUntil, setReactionCooldownUntil] = useState<number | null>(null);
  /** Affiche le bouton pour rejoindre le bas du fil quand le dernier message n’est pas visible. */
  const [showFollowLiveButton, setShowFollowLiveButton] = useState(false);

  const REACTION_COOLDOWN_MS = 2000;

  type PurchaseNotif = {
    message: string;
    icon: string;
    showIcon: boolean;
    isBuyer?: boolean;
    offerColors?: {
      backgroundColor: string;
      borderColor: string;
      textColor: string;
      iconColor: string;
      iconBackgroundColor: string;
    };
  };

  const SHORT_DURATION_MS = 3000;
  const FULL_DURATION_MS = 10000;

  type NotifState = {
    queue: PurchaseNotif[];
    current: PurchaseNotif | null;
    shownAt: number | null;
  };

  type Action =
    | { type: 'ENQUEUE'; payload: PurchaseNotif }
    | { type: 'ADVANCE' }
    | { type: 'CLOSE' };

  const initialState: NotifState = { queue: [], current: null, shownAt: null };

  function notifReducer(state: NotifState, action: Action): NotifState {
    switch (action.type) {
      case 'ENQUEUE': {
        const queue = [...state.queue, action.payload];
        if (!state.current) {
          const [next, ...rest] = queue;
          return { queue: rest, current: next ?? null, shownAt: next ? Date.now() : null };
        }
        return { ...state, queue };
      }
      case 'ADVANCE':
      case 'CLOSE': {
        if (state.queue.length === 0) {
          return { queue: [], current: null, shownAt: null };
        }
        const [next, ...rest] = state.queue;
        return { queue: rest, current: next, shownAt: Date.now() };
      }
      default:
        return state;
    }
  }

  function getTargetDurationMs(current: PurchaseNotif, hasMoreInQueue: boolean) {
    if (current.isBuyer) return FULL_DURATION_MS;
    return hasMoreInQueue ? SHORT_DURATION_MS : FULL_DURATION_MS;
  }

  const [notifState, dispatchNotif] = useReducer(notifReducer, initialState);
  const purchaseNotificationTimeoutRef = useRef<number | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesContentRef = useRef<HTMLDivElement>(null);
  const chatViewContainerRef = useRef<HTMLDivElement>(null);
  const chatHistoryFetchedRef = useRef(false);
  const chatHistoryJitterTimeoutRef = useRef<number | null>(null);
  const questionsFetchedRef = useRef(false);
  // Un SESSION_UPDATED vide le chat et réarme les refs ci-dessus, mais une ref ne relance pas un
  // effet : ce compteur force le rechargement de l'historique après le lancement du live.
  const [historyEpoch, setHistoryEpoch] = useState(0);
  const isAtBottomRef = useRef(true);
  /** Ancre de scroll : messages visibles + leur position dans le viewport, pour restaurer la vue
   *  après une mutation de la liste (Safari n'a pas le scroll anchoring natif de Chrome).
   *  Plusieurs candidats et pas un seul : le message repère peut quitter la liste (épinglé,
   *  modéré, filtré, évincé par le plafond), auquel cas on se rabat sur le suivant. */
  const scrollAnchorRef = useRef<ScrollAnchorCandidate[] | null>(null);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const ablyRealtimeHandlerRef = useRef<(message: Message) => void>(() => {});
  const allMessagesRef = useRef<Message[]>([]);
  const pinnedHydrationAttemptsRef = useRef<Map<string, number>>(new Map());
  /** Incrémenté au changement de session : abandonne les retentatives d'hydratation en cours. */
  const pinnedHydrationEpochRef = useRef(0);
  const pinMutationsRef = useRef<Map<string, PinMutation>>(new Map());
  const liveEventDeduplicatorRef = useRef(createLiveEventDeduplicator());
  const ably = useAbly();

  const dispatch = useAppDispatch();
  const {
    publicOffer,
    publicSession,
    publicSurvey,
    publicQuiz,
    publicQuizSubmission,
    attendeeInfo,
    publicEvergreen,
    openedOfferEmbedId,
  } = useAppSelector((s: RootState) => s.live);
  // Une seule instance de ChatView par page, sous la vidéo en mobile, en colonne sinon : un seul iframe.
  const showOfferEmbed = isAttendee && shouldShowOfferEmbed(openedOfferEmbedId, publicOffer);

  const attendeeEmailForPurchaseNotifications = useMemo(() => {
    const fromStore = attendeeInfo?.email?.trim();
    if (fromStore) return fromStore;
    return getSubscriptionAttendeeChatIdentity(attendeeAuthorization ?? null)?.email ?? '';
  }, [attendeeInfo?.email, attendeeAuthorization]);

  const chatWriteAccessContext = {
    chatMode,
    evergreenChatMode: publicEvergreen?.chatMode,
    isAttendee,
  };
  const isChatDisabled = isChatDisabledMode(chatWriteAccessContext);
  const isAttendeeWriteLocked = isAttendeeChatWriteLocked(chatWriteAccessContext);
  const composerTextClassName =
    visualIdentity?.defaultIdentity === true
      ? visualIdentity?.theme === 'LIGHT'
        ? 'text-black'
        : 'text-white'
      : visualIdentity?.theme === 'LIGHT'
        ? 'text-tertiary'
        : 'text-tertiary-inverse';
  const currentPublicSurveySubmission = useAppSelector(
    (s: RootState) => s.webinar.currentPublicSurveySubmission,
  );

  const hasUserSubmittedQuiz =
    publicQuizSubmission?.templateId === publicQuiz?.id &&
    (publicQuizSubmission?.answers?.length ?? 0) > 0;
  const hasUserSubmittedSurvey =
    currentPublicSurveySubmission?.templateId === publicSurvey?.id &&
    (currentPublicSurveySubmission?.answers?.length ?? 0) > 0;

  const shouldShowChatMessageInList = useCallback(
    (msg: Message, findIn: (messageId: string | number) => Message | undefined): boolean => {
      if (isAttendee) {
        // Visibilité basée sur le flag privé du message, pas sur le mode de chat courant :
        // passer en privé ne masque plus rétroactivement l'historique public déjà affiché.
        if (
          isExplicitPrivateChatPayload(msg.data) &&
          !isPrivateChatMessageVisibleToAttendee(msg, userId, findIn)
        ) {
          return false;
        }
        // Evergreen : si publicChatVisible est false, n'afficher que les messages event et ceux de l'utilisateur.
        if (
          isEvergreen &&
          publicEvergreen?.publicChatVisible === false &&
          !isEvergreenAttendeeVisibleMessage(msg, userId)
        ) {
          return false;
        }
      }
      return true;
    },
    [isAttendee, userId, isEvergreen, publicEvergreen?.publicChatVisible],
  );

  allMessagesRef.current = allMessages;

  // Lu au moment du plafonnement : un ref évite de changer l'identité des callbacks à chaque
  // épinglage, donc de relancer les effets d'historique.
  const pinnedMessagesRef = useRef<Set<string>>(pinnedMessages);
  pinnedMessagesRef.current = pinnedMessages;

  // Toujours la dernière version du filtre, pour pouvoir l’utiliser dans des effets à deps
  // stables sans les invalider.
  const shouldShowChatMessageInListRef = useRef(shouldShowChatMessageInList);
  shouldShowChatMessageInListRef.current = shouldShowChatMessageInList;

  /** Au changement de mode de chat : dédoublonne, trie et refiltre l'historique courant. */
  useEffect(() => {
    const deduped = dedupeAndSortMessages(
      allMessagesRef.current,
      keepPendingQuestions,
      pinnedMessagesRef.current,
    );
    const findIn = (id: string | number) =>
      deduped.find((m) => {
        const mid = getMessageIdFromData(m.data);
        return mid != null && mid.toString() === String(id);
      });
    const visible = deduped.filter((msg) => shouldShowChatMessageInList(msg, findIn));
    setAllMessages(deduped);
    setMessages(visible);
  }, [chatMode, shouldShowChatMessageInList, keepPendingQuestions]);

  const clearTimer = () => {
    if (purchaseNotificationTimeoutRef.current != null) {
      clearTimeout(purchaseNotificationTimeoutRef.current);
      purchaseNotificationTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    clearTimer();

    const { current, queue, shownAt } = notifState;
    if (!current || !shownAt) return;

    const hasMore = queue.length > 0;
    const target = getTargetDurationMs(current, hasMore);

    const elapsed = Date.now() - shownAt;
    const remaining = Math.max(0, target - elapsed);

    purchaseNotificationTimeoutRef.current = window.setTimeout(() => {
      dispatchNotif({ type: 'ADVANCE' });
    }, remaining);

    return clearTimer;
  }, [notifState.current, notifState.queue.length, notifState.shownAt]);

  const addPurchaseNotificationToQueue = (n: PurchaseNotif) => {
    dispatchNotif({ type: 'ENQUEUE', payload: n });
  };

  const handleClosePurchaseNotification = () => {
    clearTimer();
    dispatchNotif({ type: 'CLOSE' });
  };

  useEffect(() => clearTimer, []);

  useEffect(() => {
    if (
      publicSurvey &&
      webinarId &&
      (attendeeAuthorization ?? (isSpeaker && speakerAuthorization)) &&
      (isAttendee || isSpeaker)
    ) {
      const auth = attendeeAuthorization ?? speakerAuthorization;
      if (auth) {
        dispatch(
          fetchMyPublicSurveySubmission({
            webinarId,
            templateId: publicSurvey.id,
            authorization: auth,
          }),
        );
      }
    }
  }, [
    publicSurvey,
    webinarId,
    attendeeAuthorization,
    speakerAuthorization,
    isAttendee,
    isSpeaker,
    dispatch,
  ]);

  const chatViewer = useMemo(
    () => chatViewerFromProps({ userId, isAdmin, isSpeaker }),
    [userId, isAdmin, isSpeaker],
  );

  const applyAttendeeChatHistoryDtos = useCallback(
    (dtos: ChatMessageDto[]) => {
      if (!Array.isArray(dtos)) return;
      const cleanDtos = dtos.filter((dto) => isModeratedChatMessageVisible(dto, chatViewer));
      const historyMessages = cleanDtos.map(chatMessageDtoToMessage);
      setAllMessages((prev) =>
        mergeMessagesDeduped(
          prev,
          historyMessages,
          keepPendingQuestions,
          pinnedMessagesRef.current,
        ),
      );
      setMessages((prev) => {
        const combined = mergeMessagesDeduped(
          prev,
          historyMessages,
          keepPendingQuestions,
          pinnedMessagesRef.current,
        );
        const findInCombined = (id: string | number) =>
          combined.find((m) => {
            const mid = getMessageIdFromData(m.data);
            return mid != null && mid.toString() === String(id);
          });
        return combined.filter((msg) =>
          shouldShowChatMessageInListRef.current(msg, findInCombined),
        );
      });
      // Sans ça le bloc épinglé reste vide : l'état pin ne venait que des events Ably reçus après l'entrée.
      // L'historique est caché côté back : un désépinglage reçu depuis prime sur ce qu'il annonce.
      const pinnedIdsFromHistory = selectPinnedIdsFromHistory(cleanDtos, pinMutationsRef.current);
      if (pinnedIdsFromHistory.length > 0) {
        setPinnedMessages((prev) => new Set([...prev, ...pinnedIdsFromHistory]));
      }
      const reactionsFromHistory: Record<string, MessageReactions> = {};
      for (const dto of cleanDtos) {
        const mapped = mapDtoReactionsToMessageReactions(dto.reactions);
        if (Object.keys(mapped).length > 0) {
          reactionsFromHistory[String(dto.id)] = mapped;
        }
      }
      if (Object.keys(reactionsFromHistory).length > 0) {
        setReactionsByMessage((prev) => mergeReactionsByMessage(prev, reactionsFromHistory));
      }
    },
    [chatViewer, keepPendingQuestions],
  );

  // L'event Ably de pin ne porte que l'id : un message épinglé après l'entrée du spectateur, et posté
  // avant elle, n'est dans aucune liste locale. On recharge l'historique épinglé pour l'afficher.
  // Le back publie cet event dans la transaction de l'épinglage : la réponse peut ne pas encore le
  // porter, et un échec réseau donne le même vide. On retente donc, un nombre borné de fois.
  const hydratePinnedMessageIfMissing = useCallback(
    async (messageId: string) => {
      if (
        !(isAttendee && webinarId != null && attendeeSessionIdentifier && attendeeAuthorization)
      ) {
        return;
      }
      const hydrationEpoch = pinnedHydrationEpochRef.current;
      // Relu à chaque tentative : le message a pu arriver entre-temps par le flux normal.
      const readHydrationContext = () => ({
        loadedMessageIds: allMessagesRef.current
          .map((msg) => getMessageIdFromData(msg.data))
          .filter((id): id is string | number => id != null)
          .map((id) => id.toString()),
        attemptsByMessageId: pinnedHydrationAttemptsRef.current,
      });

      while (shouldHydratePinnedMessage(messageId, readHydrationContext())) {
        recordPinnedHydrationAttempt(messageId, pinnedHydrationAttemptsRef.current);
        try {
          const dtos = await dispatch(
            fetchChatHistory({
              webinarId,
              sessionIdentifier: attendeeSessionIdentifier,
              authorization: attendeeAuthorization,
            }),
          ).unwrap();
          if (pinnedHydrationEpochRef.current !== hydrationEpoch) return;
          applyAttendeeChatHistoryDtos(dtos);
          if (historyContainsMessageId(dtos, messageId)) return;
        } catch {
          if (pinnedHydrationEpochRef.current !== hydrationEpoch) return;
        }
        await waitBeforePinnedHydrationRetry();
        if (pinnedHydrationEpochRef.current !== hydrationEpoch) return;
      }
    },
    [
      isAttendee,
      webinarId,
      attendeeSessionIdentifier,
      attendeeAuthorization,
      dispatch,
      applyAttendeeChatHistoryDtos,
    ],
  );

  useEffect(() => {
    if (!channelName || chatHistoryFetchedRef.current) return;
    if (!(isAttendee && webinarId != null && attendeeSessionIdentifier && attendeeAuthorization)) {
      return;
    }

    const jitterStorageKey = getChatHistoryJitterStorageKey(webinarId, attendeeSessionIdentifier);
    const hasAlreadyJittered = readSessionStorageFlag(jitterStorageKey);
    const jitterMs = hasAlreadyJittered ? 0 : 1000 + Math.random() * 9000;

    const timeoutId = window.setTimeout(() => {
      chatHistoryJitterTimeoutRef.current = null;
      chatHistoryFetchedRef.current = true;
      writeSessionStorageFlag(jitterStorageKey);
      dispatch(
        fetchChatHistory({
          webinarId,
          sessionIdentifier: attendeeSessionIdentifier,
          authorization: attendeeAuthorization,
        }),
      )
        .unwrap()
        .then(applyAttendeeChatHistoryDtos)
        .catch(() => {});
    }, jitterMs);
    chatHistoryJitterTimeoutRef.current = timeoutId;

    return () => window.clearTimeout(timeoutId);
  }, [
    channelName,
    isAttendee,
    webinarId,
    attendeeSessionIdentifier,
    attendeeAuthorization,
    dispatch,
    applyAttendeeChatHistoryDtos,
    historyEpoch,
  ]);

  useEffect(() => {
    if (!channelName || chatHistoryFetchedRef.current) return;

    if (isAdmin && workspaceId != null && webinarId != null && sessionId != null) {
      chatHistoryFetchedRef.current = true;
      dispatch(
        fetchWorkspaceChatMessages({
          workspaceId,
          webinarId,
          sessionId,
        }),
      )
        .unwrap()
        .then((dtos) => {
          if (!Array.isArray(dtos)) return;
          const cleanDtos = dtos.filter((dto) => isModeratedChatMessageVisible(dto, chatViewer));
          const historyMessages = cleanDtos.map(chatMessageDtoToMessage);
          setAllMessages((prev) =>
            mergeMessagesDeduped(
              prev,
              historyMessages,
              keepPendingQuestions,
              pinnedMessagesRef.current,
            ),
          );
          setMessages((prev) =>
            mergeMessagesDeduped(
              prev,
              historyMessages,
              keepPendingQuestions,
              pinnedMessagesRef.current,
            ),
          );
          // Sans ça la régie repart sans aucun épinglage après un rechargement : l'état ne venait
          // que des events Ably reçus depuis l'ouverture de la page.
          const pinnedIdsFromHistory = selectPinnedIdsFromHistory(
            cleanDtos,
            pinMutationsRef.current,
          );
          if (pinnedIdsFromHistory.length > 0) {
            setPinnedMessages((prev) => new Set([...prev, ...pinnedIdsFromHistory]));
          }
          const reactionsFromHistory: Record<string, MessageReactions> = {};
          for (const dto of cleanDtos) {
            const mapped = mapDtoReactionsToMessageReactions(dto.reactions);
            if (Object.keys(mapped).length > 0) {
              reactionsFromHistory[String(dto.id)] = mapped;
            }
          }
          if (Object.keys(reactionsFromHistory).length > 0) {
            setReactionsByMessage((prev) => ({ ...reactionsFromHistory, ...prev }));
          }
        })
        .catch(() => {});
    }
  }, [
    channelName,
    dispatch,
    isAdmin,
    chatViewer,
    webinarId,
    workspaceId,
    sessionId,
    keepPendingQuestions,
    historyEpoch,
  ]);

  // L'historique du chat staff exclut les questions en attente côté back (findRecentStaffMessages
  // filtre question = false) : on les récupère via la route dédiée pour les réintégrer au chat.
  useEffect(() => {
    if (!isAdmin) return;
    if (workspaceId == null || webinarId == null || sessionId == null) return;
    if (questionsFetchedRef.current) return;
    questionsFetchedRef.current = true;

    const PAGE_SIZE = 100;
    const MAX_PAGES = 50;

    (async () => {
      const collected: ChatMessageDto[] = [];
      for (let page = 0; page < MAX_PAGES; page++) {
        try {
          const res = await dispatch(
            fetchWorkspaceQuestions({ workspaceId, webinarId, sessionId, page, size: PAGE_SIZE }),
          ).unwrap();
          collected.push(...res.content);
          if (res.last || res.content.length === 0) break;
        } catch {
          break;
        }
      }
      if (collected.length === 0) return;

      const questionMessages = collected.map(chatMessageDtoToMessage);
      const pinnedQuestionIds = selectPinnedIdsFromHistory(collected, pinMutationsRef.current);
      if (pinnedQuestionIds.length > 0) {
        setPinnedMessages((prev) => new Set([...prev, ...pinnedQuestionIds]));
      }
      setAllMessages((prev) => {
        const combined = mergeMessagesDeduped(
          prev,
          questionMessages,
          keepPendingQuestions,
          pinnedMessagesRef.current,
        );
        setMessages(() => {
          const findIn = (id: string | number) =>
            combined.find((m) => {
              const mid = getMessageIdFromData(m.data);
              return mid != null && mid.toString() === String(id);
            });
          return combined.filter((msg) => shouldShowChatMessageInListRef.current(msg, findIn));
        });
        return combined;
      });
    })();
  }, [isAdmin, workspaceId, webinarId, sessionId, dispatch, keepPendingQuestions, historyEpoch]);

  const scrollToBottom = () => {
    // Descente immédiate et non animée : pendant une animation, chaque message reçu déclenche le
    // recalage, qui voit une position encore éloignée du bas et ramène la vue en arrière. Sur un
    // chat actif le retour au direct n'aboutissait jamais.
    // Colonne inversée : le bas de liste, c'est zéro. Un seul chemin, et borné au conteneur du
    // chat, là où scrollIntoView aurait aussi fait défiler les conteneurs parents.
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = 0;
    }
    isAtBottomRef.current = true;
    setShowFollowLiveButton(false);
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    let captureFrame = 0;
    // Mémorise le 1er message visible et sa position dans le viewport (throttle rAF) pour pouvoir
    // restaurer la vue après une mutation de la liste.
    const captureAnchor = () => {
      captureFrame = 0;
      const containerTop = container.getBoundingClientRect().top;
      const els = Array.from(container.querySelectorAll<HTMLElement>('[data-message-id]'));
      const candidates: ScrollAnchorCandidate[] = [];
      for (const el of els) {
        // Position dans la zone visible plutôt que offsetTop : c'est la grandeur qu'on saura
        // comparer telle quelle au recalage, sans dépendre du parent de référence ni du repère
        // inversé du conteneur.
        const viewportTop = el.getBoundingClientRect().top - containerTop;
        if (viewportTop + el.offsetHeight > 0) {
          const id = el.dataset.messageId;
          if (id) {
            candidates.push({ id, viewportTop });
          }
          if (candidates.length >= ANCHOR_CANDIDATE_COUNT) break;
        }
      }
      scrollAnchorRef.current = candidates.length > 0 ? candidates : null;
    };
    const onScroll = () => {
      const lastEl = lastMessageRef.current;
      if (!lastEl?.isConnected) {
        // Liste vide : il n'y a rien à suivre, on se considère en bas. Sinon on ne conclut rien :
        // le dernier message peut être momentanément absent (épinglé, supprimé, évincé par le
        // plafond), et forcer « en bas » ramenait le lecteur au direct sans qu'il l'ait demandé.
        if (container.querySelector('[data-message-id]') == null) {
          isAtBottomRef.current = true;
          setShowFollowLiveButton(false);
        }
        return;
      }
      const elRect = lastEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const atBottom = elRect.bottom <= containerRect.bottom + 10;
      isAtBottomRef.current = atBottom;
      setShowFollowLiveButton(!atBottom);
      if (!atBottom && !captureFrame) {
        captureFrame = requestAnimationFrame(captureAnchor);
      }
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
      if (captureFrame) cancelAnimationFrame(captureFrame);
    };
  }, []);

  // Émule le scroll anchoring de Chrome (absent de Safari) : avant le paint, on recale la vue.
  const restoreScrollAnchor = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    // Collé en bas : on suit le live. Colonne inversée, donc zéro est le bas de liste. On ne
    // passe pas par scrollIntoView, qui fait aussi défiler les conteneurs parents : appelé
    // depuis l'observateur de taille (clavier mobile, sondage qui s'ouvre), il déplaçait toute
    // la page live pour ramener le dernier message à l'écran.
    if (isAtBottomRef.current) {
      container.scrollTop = 0;
      return;
    }
    // Au milieu : on garde le message ancré au même pixel malgré l'ajout en bas, le retrait du
    // message le plus ancien (cap 300) et le passage d'un message dans le bloc épinglé. Le premier
    // candidat encore présent fait foi : sans ce repli, un épinglage ou une suppression du message
    // repère renvoyait la vue n'importe où.
    const findMessageEl = (id: string) =>
      container.querySelector<HTMLElement>(`[data-message-id="${CSS.escape(id)}"]`);
    const anchor = selectSurvivingAnchor(
      scrollAnchorRef.current,
      (id) => findMessageEl(id) != null,
    );
    if (!anchor) return;
    const el = findMessageEl(anchor.id);
    if (!el) return;
    // Correction relative : on rattrape l'écart constaté, on ne reconstitue pas une position
    // absolue. Une position absolue mal reconstituée envoyait la vue en bas de liste, puisqu'une
    // valeur hors plage est ramenée à zéro, et zéro est le bas dans une colonne inversée.
    const currentViewportTop =
      el.getBoundingClientRect().top - container.getBoundingClientRect().top;
    const correction = resolveScrollCorrection(currentViewportTop, anchor, container.clientHeight);
    if (correction !== 0) {
      container.scrollTop += correction;
    }
  }, []);

  useLayoutEffect(() => {
    restoreScrollAnchor();
  }, [messages, pinnedMessages, restoreScrollAnchor]);

  // Deux hauteurs bougent sans que la liste change, et le lecteur se retrouvait déplacé sans
  // l'avoir demandé. Celle du contenu, surtout en régie : zone de réponse dépliée dans une bulle,
  // confirmation de blocage, résultats d'un sondage. Et celle du panneau lui-même : sondage, quiz
  // ou offre qui s'ouvre à côté du chat, bloc épinglé qui grandit, clavier mobile. Le conteneur
  // étant en colonne inversée, le rétrécir garde le bas collé, donc la vue redescendait vers le
  // direct. On observe les deux plutôt que d'énumérer les états qui les font varier.
  useEffect(() => {
    const content = messagesContentRef.current;
    const container = messagesContainerRef.current;
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => restoreScrollAnchor());
    if (content) observer.observe(content);
    if (container) observer.observe(container);
    return () => observer.disconnect();
  }, [restoreScrollAnchor]);

  ablyRealtimeHandlerRef.current = (message) => {
    if (!message) return;

    const normalizedMessages: Message[] =
      message.name === 'batch-events' && Array.isArray(message.data)
        ? message.data
            .filter(
              (event): event is { name: string; data: unknown } =>
                !!event && typeof event === 'object' && typeof (event as any).name === 'string',
            )
            .map((event, index) => ({
              ...message,
              // L'id Ably change à chaque republication du buffer : seul l'eventId du back permet
              // de reconnaître un message déjà affiché.
              id: readLiveEventId(event) ?? `${message.id ?? 'batch'}:${index}`,
              name: event.name,
              data: event.data,
            }))
        : [message];

    for (const rawMsg of normalizedMessages) {
      // L'id porte l'eventId du back quand il existe : un lot republié est alors ignoré. Sinon il
      // reste unique par publication Ably, donc rien n'est filtré à tort.
      if (!liveEventDeduplicatorRef.current.shouldProcess(rawMsg?.id)) {
        continue;
      }
      const rawData = rawMsg?.data;
      const normalizedData =
        rawData != null && typeof rawData === 'object' && !Array.isArray(rawData)
          ? normalizeAblyChatMessagePayload(rawData)
          : rawData;
      const currentMessage = { ...rawMsg, data: normalizedData } as Message;
      const data: any = currentMessage?.data;

      if (
        currentMessage.name === 'live-event' &&
        data &&
        typeof data === 'object' &&
        data.eventType === 'SESSION_UPDATED'
      ) {
        setMessages([]);
        setAllMessages([]);
        setReactionsByMessage({});
        setPinnedMessages(new Set());
        pinnedHydrationAttemptsRef.current = new Map();
        pinnedHydrationEpochRef.current += 1;
        pinMutationsRef.current = new Map();
        setReplyingTo(null);
        isPrivateReplyRef.current = false;
        setIsPrivateReply(false);
        setReplyText('');
        setBlockingMessageId(null);
        setSurveysShowingResults(new Set());
        dispatchNotif({ type: 'CLOSE' });
        if (chatHistoryJitterTimeoutRef.current != null) {
          window.clearTimeout(chatHistoryJitterTimeoutRef.current);
          chatHistoryJitterTimeoutRef.current = null;
        }
        chatHistoryFetchedRef.current = false;
        questionsFetchedRef.current = false;
        setHistoryEpoch((epoch) => epoch + 1);
        return;
      }

      //EVERGREEN
      if (
        currentMessage.name === 'live-event' &&
        data &&
        typeof data === 'object' &&
        data.resourceType === 'INLINE' &&
        (data.eventType === 'MESSAGE_PIN' || data.eventType === 'MESSAGE_UNPIN') &&
        typeof data.resourceId === 'number'
      ) {
        const idStr = String(data.resourceId);
        if (data.eventType === 'MESSAGE_PIN') {
          pinMutationsRef.current.set(idStr, 'pin');
          setPinnedMessages((prev) => new Set([...prev, idStr]));
        } else {
          pinMutationsRef.current.set(idStr, 'unpin');
          setPinnedMessages((prev) => {
            const next = new Set(prev);
            next.delete(idStr);
            return next;
          });
        }
        continue;
      }

      //EVERGREEN
      if (
        currentMessage.name === 'live-event' &&
        data &&
        typeof data === 'object' &&
        data.resourceType === 'INLINE' &&
        data.eventType === 'CHAT_REACTION' &&
        typeof data.resourceId === 'number'
      ) {
        let reaction: string | undefined;
        if (typeof data.reaction === 'string' && data.reaction.trim().length > 0) {
          reaction = data.reaction;
        } else if (data.payloadJson) {
          try {
            const payload =
              typeof data.payloadJson === 'string'
                ? JSON.parse(data.payloadJson)
                : data.payloadJson;
            if (
              payload &&
              typeof payload === 'object' &&
              typeof payload.reaction === 'string' &&
              payload.reaction.trim().length > 0
            ) {
              reaction = payload.reaction;
            }
          } catch (error) {
            console.error('Error parsing CHAT_REACTION payloadJson:', error, data.payloadJson);
          }
        }

        if (reaction) {
          const messageId = String(data.resourceId);
          setReactionsByMessage((prev) => {
            const prevForMessage = prev[messageId] ?? {};
            const nextForMessage: MessageReactions = {
              ...prevForMessage,
              [reaction]: (prevForMessage[reaction] ?? 0) + 1,
            };
            return {
              ...prev,
              [messageId]: nextForMessage,
            };
          });
        }
        continue;
      }

      if (data && typeof data === 'object') {
        if (
          isSpeaker &&
          webinarId &&
          speakerAuthorization &&
          data.resourceType === 'WEBINAR_RESOURCE'
        ) {
          if (data.eventType === 'RESOURCE_SHOW') {
            dispatch(
              fetchPublicResource({
                webinarId: webinarId!,
                resourceId: data.resourceId,
                authorization: attendeeAuthorization ?? '',
              }),
            ).unwrap();
          } else if (data.eventType === 'RESOURCE_HIDE') {
            dispatch(clearPublicResource());
          }
        }

        if (
          isSpeaker &&
          webinarId &&
          speakerAuthorization &&
          data.resourceType === 'QUIZ_TEMPLATE'
        ) {
          if (data.eventType === 'RESOURCE_SHOW') {
            dispatch(
              fetchPublicQuiz({
                webinarId,
                quizId: data.resourceId,
                authorization: speakerAuthorization,
              }),
            );
          } else if (data.eventType === 'RESOURCE_HIDE') {
            dispatch(clearPublicQuiz());
          }
        }

        if (
          isSpeaker &&
          webinarId &&
          speakerAuthorization &&
          data.resourceType === 'SURVEY_TEMPLATE'
        ) {
          if (data.eventType === 'RESOURCE_SHOW') {
            dispatch(
              fetchPublicSurvey({
                webinarId,
                surveyId: data.resourceId,
                authorization: speakerAuthorization,
              }),
            );
          } else if (data.eventType === 'RESOURCE_HIDE') {
            dispatch(clearPublicSurvey());
          }
        }

        if (
          isSpeaker &&
          webinarId &&
          speakerAuthorization &&
          data.resourceType === 'OFFER_TEMPLATE'
        ) {
          if (data.eventType === 'RESOURCE_SHOW') {
            dispatch(
              fetchPublicOffer({
                webinarId,
                offerId: data.resourceId,
                authorization: speakerAuthorization,
              }),
            );
          } else if (data.eventType === 'RESOURCE_HIDE') {
            dispatch(clearPublicOffer());
          }
        }

        // EVERGREEN
        if (
          currentMessage.name === 'live-event' &&
          isAttendee &&
          isEvergreen &&
          webinarId &&
          attendeeAuthorization &&
          attendeeSessionIdentifier &&
          typeof data.resourceId === 'number'
        ) {
          const rt = data.resourceType;
          const et = data.eventType;
          const isEvergreenTemplateEvent =
            (rt === 'SURVEY_TEMPLATE' && et === 'SURVEY') ||
            (rt === 'QUIZ_TEMPLATE' && et === 'QUIZ') ||
            (rt === 'OFFER_TEMPLATE' && et === 'OFFER');
          if (isEvergreenTemplateEvent) {
            let hide = false;
            try {
              if (data.payloadJson) {
                const payload =
                  typeof data.payloadJson === 'string'
                    ? JSON.parse(data.payloadJson)
                    : data.payloadJson;
                hide = payload?.action === 'HIDE';
              }
            } catch (error) {
              console.error('Evergreen template payloadJson:', error, data.payloadJson);
            }

            dispatch(
              fetchPublicResourceState({
                webinarId,
                sessionIdentifier: attendeeSessionIdentifier,
                authorization: attendeeAuthorization,
                coalesce: true,
              }),
            );

            if (hide) {
              if (rt === 'OFFER_TEMPLATE') {
                dispatch(hidePublicOffer(data.resourceId));
              } else if (rt === 'SURVEY_TEMPLATE') {
                dispatch(hidePublicSurvey(data.resourceId));
              } else if (rt === 'QUIZ_TEMPLATE') {
                dispatch(hidePublicQuiz(data.resourceId));
              }
            } else {
              if (rt === 'OFFER_TEMPLATE') {
                dispatch(
                  fetchPublicOffer({
                    webinarId,
                    offerId: data.resourceId,
                    authorization: attendeeAuthorization,
                  }),
                );
                dispatch(
                  postAnalyticsEvent({
                    webinarId,
                    sessionIdentifier: attendeeSessionIdentifier,
                    authorization: attendeeAuthorization,
                    input: {
                      eventType: 'OFFER_SEEN',
                      payloadJson: JSON.stringify({ offerId: data.resourceId }),
                      occurredAt: new Date().toISOString(),
                    },
                  }),
                );
              } else if (rt === 'SURVEY_TEMPLATE') {
                dispatch(
                  fetchPublicSurvey({
                    webinarId,
                    surveyId: data.resourceId,
                    authorization: attendeeAuthorization,
                  }),
                );
              } else if (rt === 'QUIZ_TEMPLATE') {
                dispatch(
                  fetchPublicQuiz({
                    webinarId,
                    quizId: data.resourceId,
                    authorization: attendeeAuthorization,
                  }),
                );
              }
            }
          }
        }

        if (
          isAttendee &&
          data.resourceType === 'SURVEY_TEMPLATE' &&
          (data.eventType === 'SHOW_SURVEY_RESPONSE' ||
            data.eventType === 'HIDE_SURVEY_RESPONSE') &&
          onSurveyResultsEvent
        ) {
          let timeNow = new Date().getTime();
          try {
            if (data.payloadJson) {
              let payload: any;
              if (typeof data.payloadJson === 'string') {
                payload = JSON.parse(data.payloadJson);
              } else {
                payload = data.payloadJson;
              }

              if (payload && payload.timeNow && typeof payload.timeNow === 'number') {
                timeNow = payload.timeNow;
              }
            }
          } catch (error) {
            console.error('Error parsing payloadJson:', error, data.payloadJson);
          }

          onSurveyResultsEvent({
            eventType: data.eventType as 'SHOW_SURVEY_RESPONSE' | 'HIDE_SURVEY_RESPONSE',
            templateId: data.resourceId,
            timeNow,
            onUpdate: (templateId: number, show: boolean) => {
              setSurveysShowingResults((prev) => {
                const newSet = new Set(prev);
                if (show) {
                  newSet.add(templateId);
                } else {
                  newSet.delete(templateId);
                }
                return newSet;
              });
            },
          });
        }

        if (
          isAttendee &&
          !isEvergreen &&
          webinarId &&
          attendeeAuthorization &&
          (data.resourceType === 'OFFER_TEMPLATE' ||
            data.resourceType === 'SURVEY_TEMPLATE' ||
            data.resourceType === 'QUIZ_TEMPLATE') &&
          onResourceEvent
        ) {
          let timeNow = new Date().getTime();
          try {
            if (data.payloadJson) {
              let payload: any;
              if (typeof data.payloadJson === 'string') {
                payload = JSON.parse(data.payloadJson);
              } else {
                payload = data.payloadJson;
              }

              if (payload && payload.timeNow && typeof payload.timeNow === 'number') {
                timeNow = payload.timeNow;
              }
            }
          } catch (error) {
            console.error('Error parsing payloadJson:', error, data.payloadJson);
          }

          onResourceEvent({
            eventType: data.eventType,
            resourceType: data.resourceType,
            resourceId: data.resourceId,
            timeNow,
          });
        }
      }

      if (data && typeof data === 'object' && data.type === 'PURCHASE_NOTIFICATION') {
        if (isAttendee || isAdmin) {
          const purchaseNotification = resolvePurchaseNotificationDisplay(data, {
            viewer: isAttendee ? 'ATTENDEE' : 'ADMIN',
            attendeeEmail: attendeeEmailForPurchaseNotifications,
            evergreenMessage: tChat('purchaseNotificationEvergreen'),
          });

          if (purchaseNotification) {
            addPurchaseNotificationToQueue({
              message: purchaseNotification.message,
              icon: purchaseNotification.icon,
              showIcon: purchaseNotification.showIcon,
              isBuyer: purchaseNotification.isBuyer,
              offerColors: extractPurchaseNotificationOfferColors(purchaseNotification.colorSource),
            });
          }
        }
      }

      if (
        data &&
        typeof data === 'object' &&
        data.type === 'reaction' &&
        (data.messageId != null || data.id != null) &&
        data.emoji
      ) {
        const messageId = (data.id ?? data.messageId)?.toString() ?? '';
        const { emoji } = data as { emoji: string };

        setReactionsByMessage((prev) => {
          const prevForMessage = prev[messageId] ?? {};
          const nextForMessage: MessageReactions = {
            ...prevForMessage,
            [emoji]: (prevForMessage[emoji] ?? 0) + 1,
          };

          return {
            ...prev,
            [messageId]: nextForMessage,
          };
        });

        continue;
      }

      // Marquage manuel d'un message comme question, et résolution (WELYA-284). Depuis la fusion
      // des onglets, la question reste dans le chat : elle change seulement d'apparence.
      if (data && typeof data === 'object' && data.type === CHAT_QUESTION_STATE_EVENT) {
        const actionValue = (data as { action?: string }).action;
        const questionDto = (data as { message?: ChatMessageDto }).message;
        const stateMessageId = (
          questionDto?.id ?? (data as { messageId?: number }).messageId
        )?.toString();
        if (!stateMessageId) continue;

        // Côté spectateur rien ne bouge : un message question reste un message ordinaire.
        if (isAdmin) {
          if (actionValue === CHAT_QUESTION_ACTION_MOVED) {
            const movedQuestion = questionDto ? chatMessageDtoToMessage(questionDto) : null;
            setAllMessages((prevAll) => {
              // Le back propage question=true aux réponses directes sans event Ably dédié.
              const patched = prevAll.map((m) => {
                if (isDirectReplyToMessage(m, stateMessageId)) {
                  return patchMessageActiveQuestionState(m);
                }
                if (
                  movedQuestion == null &&
                  getMessageIdFromData(m.data)?.toString() === stateMessageId
                ) {
                  return patchMessageActiveQuestionState(m);
                }
                return m;
              });
              const combined =
                movedQuestion == null
                  ? dedupeAndSortMessages(patched, keepPendingQuestions, pinnedMessagesRef.current)
                  : mergeMessagesDeduped(
                      patched.filter(
                        (m) => getMessageIdFromData(m.data)?.toString() !== stateMessageId,
                      ),
                      [movedQuestion],
                      keepPendingQuestions,
                      pinnedMessagesRef.current,
                    );
              setMessages(() => {
                const findIn = (id: string | number) =>
                  combined.find((m) => {
                    const mid = getMessageIdFromData(m.data);
                    return mid != null && mid.toString() === String(id);
                  });
                return combined.filter((msg) => shouldShowChatMessageInList(msg, findIn));
              });
              return combined;
            });
          } else if (actionValue === CHAT_QUESTION_ACTION_HANDLED && questionDto) {
            const handledQuestion = chatMessageDtoToMessage(questionDto);
            setAllMessages((prevAll) => {
              const withoutStale = prevAll.filter(
                (m) => getMessageIdFromData(m.data)?.toString() !== stateMessageId,
              );
              const withHandledReplies = withoutStale.map((m) =>
                isDirectReplyToMessage(m, stateMessageId) ? patchMessageQuestionHandledState(m) : m,
              );
              const combined = mergeMessagesDeduped(
                withHandledReplies,
                [handledQuestion],
                keepPendingQuestions,
                pinnedMessagesRef.current,
              );
              setMessages(() => {
                const findIn = (id: string | number) =>
                  combined.find((m) => {
                    const mid = getMessageIdFromData(m.data);
                    return mid != null && mid.toString() === String(id);
                  });
                return combined.filter((msg) => shouldShowChatMessageInList(msg, findIn));
              });
              return combined;
            });
          }
        }
        continue;
      }

      if (data && typeof data === 'object' && data.type === 'pin') {
        const messageId = getMessageIdFromData(data)?.toString();
        if (messageId) {
          pinMutationsRef.current.set(messageId, 'pin');
          setPinnedMessages((prev) => new Set([...prev, messageId]));
          hydratePinnedMessageIfMissing(messageId);
        }
        continue;
      }

      if (data && typeof data === 'object' && data.type === 'unpin') {
        const messageId = getMessageIdFromData(data)?.toString();
        if (messageId) {
          pinMutationsRef.current.set(messageId, 'unpin');
          setPinnedMessages((prev) => {
            const next = new Set(prev);
            next.delete(messageId);
            return next;
          });
        }
        continue;
      }

      if (data && typeof data === 'object' && data.type === 'delete') {
        const messageId = getMessageIdFromData(data)?.toString();
        if (messageId) {
          const shouldRemove = (msg: Message): boolean => {
            const msgId = getMessageIdFromData(msg.data)?.toString();
            if (msgId !== messageId) return false;
            const msgData: any = msg.data;
            if (isAdmin && msgData?.banned === true) return false;
            return true;
          };
          setAllMessages((prevAllMessages) => prevAllMessages.filter((msg) => !shouldRemove(msg)));
          setMessages((prevMessages) => prevMessages.filter((msg) => !shouldRemove(msg)));
          pinMutationsRef.current.set(messageId, 'unpin');
          setPinnedMessages((prev) => {
            const next = new Set(prev);
            next.delete(messageId);
            return next;
          });
        }
        continue;
      }

      if (data && typeof data === 'object' && data.type === 'block' && data.userId) {
        continue;
      }

      if (currentMessage.name === 'live-reaction') {
        continue;
      }

      // Les réponses (dont privées) peuvent être poussées sous ce nom ; ne pas les ignorer.
      // Autoriser les payloads typés tant qu’ils portent un texte exploitable (évite de tout
      // jeter via `data.type` alors qu’il s’agit d’un message chat).
      if (
        data &&
        typeof data === 'object' &&
        data.type != null &&
        data.type !== '' &&
        !(
          (typeof (data as { text?: string }).text === 'string' &&
            (data as { text: string }).text.trim()) ||
          (typeof (data as { content?: string }).content === 'string' &&
            (data as { content: string }).content.trim())
        )
      ) {
        continue;
      }

      if (data && typeof data === 'object' && data.reaction === true) {
        continue;
      }

      const hasContent =
        (typeof data === 'string' && data.trim().length > 0) ||
        (data && typeof data === 'object' && (data.text?.trim() || data.content?.trim()));

      if (hasContent) {
        setAllMessages((prevAllMessages) => {
          const combined = mergeMessagesDeduped(
            prevAllMessages,
            [currentMessage],
            keepPendingQuestions,
            pinnedMessagesRef.current,
          );
          setMessages((prevMessages) => {
            const merged = mergeMessagesDeduped(
              prevMessages,
              [currentMessage],
              keepPendingQuestions,
              pinnedMessagesRef.current,
            );
            const findIn = (id: string | number) =>
              combined.find((m) => {
                const mid = getMessageIdFromData(m.data);
                return mid != null && mid.toString() === String(id);
              });
            return merged.filter((msg) => shouldShowChatMessageInList(msg, findIn));
          });
          return combined;
        });
      }
    }
  };

  useChannel(channelName, (message) => {
    ablyRealtimeHandlerRef.current(message);
  });

  /* Canal « -staff » : messages privés / DM (backend y publie surtout en mode PRIVATE). */
  useEffect(() => {
    const listensToStaffChannel = isAttendee || isAdmin || isSpeaker;
    if (!listensToStaffChannel || !channelName.trim()) return;
    const staffChannel = ablyPrivateChatChannelName(channelName);
    if (!staffChannel) return;
    const forward = (msg: Message) => {
      ablyRealtimeHandlerRef.current(msg);
    };
    const ch = ably.channels.get(staffChannel);
    ch.subscribe(forward);
    return () => {
      ch.unsubscribe(forward);
    };
  }, [ably, channelName, isAttendee, isAdmin, isSpeaker]);

  const handleStartReply = (messageId?: string | number | null) => {
    if (!messageId) return;
    const idStr = messageId.toString();
    setReplyingTo(idStr);
    isPrivateReplyRef.current = false;
    setIsPrivateReply(false);
    setReplyText('');
  };

  const handlePrivateStartReply = (messageId?: string | number | null) => {
    if (!messageId) return;
    const idStr = messageId.toString();
    setReplyingTo(idStr);
    isPrivateReplyRef.current = true;
    setIsPrivateReply(true);
    setReplyText('');
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    isPrivateReplyRef.current = false;
    setIsPrivateReply(false);
    setReplyText('');
  };

  useEffect(() => {
    if (!reactionCooldownUntil) return;
    const remaining = reactionCooldownUntil - Date.now();
    if (remaining <= 0) {
      setReactionCooldownUntil(null);
      return;
    }
    const t = window.setTimeout(() => setReactionCooldownUntil(null), remaining);
    return () => clearTimeout(t);
  }, [reactionCooldownUntil]);

  const handleReaction = async (messageId: number | undefined, emoji: string) => {
    if (!messageId || isSending) return;
    if (reactionCooldownUntil != null && Date.now() < reactionCooldownUntil) return;

    if (isAttendee && webinarId && attendeeSessionIdentifier && attendeeAuthorization) {
      setIsSending(true);
      const playbackAt = toPlaybackAt(getPlaybackTimestamp?.());
      try {
        await dispatch(
          postChatReaction({
            webinarId,
            sessionIdentifier: attendeeSessionIdentifier,
            authorization: attendeeAuthorization,
            input: {
              messageId: messageId.toString(),
              reaction: emoji,
            },
          }),
        ).unwrap();

        dispatch(
          postAnalyticsEvent({
            webinarId,
            sessionIdentifier: attendeeSessionIdentifier,
            authorization: attendeeAuthorization,
            input: {
              eventType: 'CHAT_REACTION',
              payloadJson: buildChatReactionAnalyticsPayload(messageId, emoji, playbackAt),
              occurredAt: new Date().toISOString(),
            },
          }),
        );
        setReactionCooldownUntil(Date.now() + REACTION_COOLDOWN_MS);
      } finally {
        setIsSending(false);
      }
    }
  };

  const handlePinMessage = async (messageId: number | undefined) => {
    if (!messageId || !webinarId || !workspaceId || !sessionId) return;

    const messageIdStr = messageId.toString();
    const isCurrentlyPinned = pinnedMessages.has(messageIdStr);

    try {
      if (isAdmin && workspaceId && sessionId) {
        if (isCurrentlyPinned) {
          await dispatch(
            unpinWorkspaceChatMessage({
              workspaceId,
              webinarId,
              sessionId,
              messageId,
            }),
          ).unwrap();
        } else {
          await dispatch(
            pinWorkspaceChatMessage({
              workspaceId,
              webinarId,
              sessionId,
              messageId,
            }),
          ).unwrap();
        }
      }
    } catch (error) {
      console.error('Error pinning/unpinning message:', error);
    }
  };

  const handleDeleteMessage = async (messageId: number | undefined) => {
    if (!messageId || !webinarId || !workspaceId || !sessionId) return;

    try {
      if (isAdmin && workspaceId && sessionId) {
        await dispatch(
          deleteWorkspaceChatMessage({
            workspaceId,
            webinarId,
            sessionId,
            messageId,
          }),
        ).unwrap();
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };
  const handleOfferLinkClick = (url: string) => {
    if (!isAttendee || !webinarId || !attendeeSessionIdentifier || !attendeeAuthorization) return;
    dispatch(
      postAnalyticsEvent({
        webinarId,
        sessionIdentifier: attendeeSessionIdentifier,
        authorization: attendeeAuthorization,
        input: {
          eventType: 'OFFER_CLICK',
          payloadJson: JSON.stringify({ clickedUrl: url }),
          occurredAt: new Date().toISOString(),
        },
      }),
    );
  };

  // Déplacement / résolution : on délègue la mise à jour locale aux events Ably (comme delete/pin),
  // pour rester cohérent entre tous les clients (onglet Chat ET onglet Questions).
  const handleMoveToQuestions = async (messageId: number | undefined) => {
    if (!messageId || !webinarId || !workspaceId || !sessionId || !isAdmin) return;
    try {
      await dispatch(
        moveWorkspaceMessageToQuestions({ workspaceId, webinarId, sessionId, messageId }),
      ).unwrap();
    } catch (error) {
      console.error('Error moving message to questions:', error);
      toast.error(t('adminLive.chat.moveToQuestionsError'));
    }
  };

  const handleValidateQuestion = async (messageId: number | undefined) => {
    if (!messageId || !webinarId || !workspaceId || !sessionId || !isAdmin) return;
    try {
      await dispatch(
        validateWorkspaceQuestion({ workspaceId, webinarId, sessionId, messageId }),
      ).unwrap();
    } catch (error) {
      console.error('Error validating question:', error);
      toast.error(t('adminLive.chat.markAsHandledError'));
    }
  };

  const handleSendReply = async () => {
    if (!replyingTo || !replyText.trim() || isSending) return;
    if (replyText.trim().length > CHAT_MESSAGE_MAX_LENGTH) return;

    const repliedToIdNumber = Number(replyingTo);
    if (Number.isNaN(repliedToIdNumber)) {
      console.warn('Invalid repliedToMessageId', replyingTo);
      return;
    }

    const sendPrivate = isPrivateReplyRef.current || isPrivateReply;
    const messageInput: PostChatMessageInput = {
      content: replyText.trim(),
      repliedToMessageId: repliedToIdNumber,
      playbackAt: toPlaybackAt(getPlaybackTimestamp?.()),
    };
    if (sendPrivate) {
      messageInput.privateMessage = true;
    }

    setIsSending(true);
    try {
      const mergePostedChatDto = (dto: ChatMessageDto) => {
        const currentMessage = chatMessageDtoToMessage(dto);
        setAllMessages((prevAllMessages) => {
          const combined = mergeMessagesDeduped(
            prevAllMessages,
            [currentMessage],
            keepPendingQuestions,
            pinnedMessagesRef.current,
          );
          setMessages((prevMessages) => {
            const merged = mergeMessagesDeduped(
              prevMessages,
              [currentMessage],
              keepPendingQuestions,
              pinnedMessagesRef.current,
            );
            const findIn = (id: string | number) =>
              combined.find((m) => {
                const mid = getMessageIdFromData(m.data);
                return mid != null && mid.toString() === String(id);
              });
            return merged.filter((msg) => shouldShowChatMessageInList(msg, findIn));
          });
          return combined;
        });
      };

      if (isAttendee && webinarId && attendeeSessionIdentifier && attendeeAuthorization) {
        const dto = await dispatch(
          postAttendeeChatMessage({
            webinarId,
            sessionIdentifier: attendeeSessionIdentifier,
            authorization: attendeeAuthorization,
            input: messageInput,
          }),
        ).unwrap();
        mergePostedChatDto(dto);
      } else if (isAdmin && workspaceId && webinarId && sessionId) {
        const dto = await dispatch(
          postWorkspaceChatMessage({
            workspaceId,
            webinarId,
            sessionId,
            input: messageInput,
          }),
        ).unwrap();
        mergePostedChatDto(dto);
      }

      setReplyText('');
      setReplyingTo(null);
      isPrivateReplyRef.current = false;
      setIsPrivateReply(false);
    } catch (error) {
      // La zone de réponse reste ouverte : le texte saisi n'est pas perdu.
      console.error('Error sending chat reply:', error);
      toast.error(tChat(resolveReplyErrorKey(error)));
    } finally {
      setIsSending(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isSending) return;
    if (inputMessage.trim().length > CHAT_MESSAGE_MAX_LENGTH) return;

    setIsSending(true);
    try {
      if (isAdmin && workspaceId && webinarId && sessionId) {
        await dispatch(
          postWorkspaceChatMessage({
            workspaceId,
            webinarId,
            sessionId,
            input: {
              content: inputMessage.trim(),
            },
          }),
        ).unwrap();
      }

      if (isAttendee && webinarId && attendeeSessionIdentifier && attendeeAuthorization) {
        const result = await dispatch(
          postAttendeeChatMessage({
            webinarId,
            sessionIdentifier: attendeeSessionIdentifier,
            authorization: attendeeAuthorization,
            input: {
              content: inputMessage.trim(),
              playbackAt: toPlaybackAt(getPlaybackTimestamp?.()),
            },
          }),
        ).unwrap();

        const backendMessageId = result.id?.toString();

        dispatch(
          postAnalyticsEvent({
            webinarId,
            sessionIdentifier: attendeeSessionIdentifier,
            authorization: attendeeAuthorization,
            input: {
              eventType: 'CHAT_MESSAGE',
              payloadJson: JSON.stringify({
                messageId: backendMessageId,
              }),
              occurredAt: new Date().toISOString(),
            },
          }),
        );
      }

      setInputMessage('');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isSending) handleSendMessage();
    }
  };

  const handleBlockUser = (messageId: number | undefined) => {
    if (!messageId) return;
    setBlockingMessageId(messageId);
  };

  const handleConfirmBlock = async (subscriptionId: number | undefined) => {
    if (subscriptionId == null) return;

    const msg = allMessages.find((m) => getSubscriptionIdFromData(m.data) === subscriptionId);
    const isCurrentlyBanned =
      msg?.data && typeof msg.data === 'object' && (msg.data as any)?.banned === true;

    try {
      if (
        !isCurrentlyBanned &&
        blockingMessageId != null &&
        workspaceId &&
        webinarId &&
        sessionId
      ) {
        await dispatch(
          deleteWorkspaceChatMessage({
            workspaceId,
            webinarId,
            sessionId,
            messageId: blockingMessageId,
          }),
        ).unwrap();
      }
      await dispatch(
        banPresenceParticipant({
          workspaceId: workspaceId!,
          webinarId: webinarId!,
          sessionId: sessionId!,
          subscriptionId,
        }),
      ).unwrap();
      setBlockingMessageId(null);
      toast.success(
        isCurrentlyBanned ? t('adminLive.chat.userUnblocked') : t('adminLive.chat.userBlocked'),
      );
    } catch (error) {
      console.error('Error banning presence participant:', error);
    }
  };

  const handleCancelBlock = () => {
    setBlockingMessageId(null);
  };

  const isMessageVisible = (msg: Message): boolean => {
    const msgData: any = msg.data;
    const msgUserId = msgData?.userId != null ? String(msgData.userId) : undefined;
    const isBanned = msgData?.banned === true;

    if (isBanned) {
      if (userId && msgUserId && String(userId) === msgUserId) return true;
      return false;
    }

    return isModeratedChatMessageVisible(msgData, chatViewer);
  };

  const pinnedMessagesList = messages.filter((msg) => {
    const messageId = getMessageIdFromData(msg.data)?.toString();
    const isPinned = messageId && pinnedMessages.has(messageId);
    return isPinned && isMessageVisible(msg);
  });

  const unpinnedMessagesList = messages.filter((msg) => {
    const messageId = getMessageIdFromData(msg.data)?.toString();
    const isNotPinned = !messageId || !pinnedMessages.has(messageId);
    return isNotPinned && isMessageVisible(msg);
  });

  const purchaseNotificationCard = notifState.current && (
    <div className="p-2">
      <PopupBuyerCard
        message={notifState.current.message}
        icon={notifState.current.icon}
        showIcon={notifState.current.showIcon}
        visualIdentity={visualIdentity}
        onClose={handleClosePurchaseNotification}
        offerColors={notifState.current.offerColors}
      />
    </div>
  );

  return (
    <div
      ref={chatViewContainerRef}
      className={cn('h-full flex flex-col min-h-0 relative', isSpeaker ? 'pb-2' : 'pb-0')}
      style={{
        backgroundColor:
          visualIdentity?.theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
      }}
    >
      {notifState.current && !showOfferEmbed && purchaseNotificationCard}

      {/* Recouvre le chat sans le démonter : fil, brouillon et abonnements restent intacts. */}
      {showOfferEmbed && publicOffer && (
        <div
          className="absolute inset-0 z-20 flex flex-col min-h-0"
          style={{ backgroundColor: visualIdentity?.backgroundColor ?? '#FFFFFF' }}
        >
          {notifState.current && purchaseNotificationCard}
          <OfferEmbedPanel
            key={publicOffer.id}
            offer={publicOffer}
            visualIdentity={visualIdentity}
            onClose={() => dispatch(closeOfferEmbed())}
            stripeCheckout={
              !isEvergreen && webinarId != null && attendeeAuthorization
                ? { webinarId, authorization: attendeeAuthorization }
                : null
            }
            className="flex-1"
          />
        </div>
      )}

      {(isAttendee || isSpeaker) && publicOffer && publicSession?.visualIdentity && (
        <div className="p-2 hidden compact-landscape:block lg:block">
          <OfferCard
            offer={publicOffer}
            visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
            attendeeView
            webinarId={webinarId}
            sessionIdentifier={attendeeSessionIdentifier}
            authorization={attendeeAuthorization}
            onEmbedOpen={isAttendee ? () => dispatch(openOfferEmbed(publicOffer.id)) : undefined}
          />
        </div>
      )}

      {(isAttendee || isSpeaker) &&
        publicSurvey &&
        publicSession?.visualIdentity &&
        publicSession?.session &&
        !hasUserSubmittedSurvey && (
          <div className="p-2 hidden compact-landscape:block lg:block">
            <SurveyCard
              survey={publicSurvey}
              visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
              isAttendee
              showResults={surveysShowingResults.has(publicSurvey.id)}
              webinarId={webinarId}
              workspaceId={publicSession.session.workspaceId}
              sessionId={publicSession.session.id}
              authorization={attendeeAuthorization}
              sessionIdentifier={attendeeSessionIdentifier}
              liveParticipantCountSync={liveParticipantCountSync}
            />
          </div>
        )}

      {(isAttendee || isSpeaker) &&
        publicQuiz &&
        publicSession?.visualIdentity &&
        publicSession?.session &&
        !hasUserSubmittedQuiz && (
          <>
            {publicQuiz.enabledDuringLive === false || isChatDisabled ? (
              <div className="p-2 hidden compact-landscape:block lg:block">
                <QuizCard
                  quiz={publicQuiz}
                  visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
                  isAttendee
                  webinarId={webinarId}
                  sessionId={publicSession.session.id}
                  authorization={attendeeAuthorization}
                  sessionIdentifier={attendeeSessionIdentifier}
                  liveParticipantCountSync={liveParticipantCountSync}
                />
              </div>
            ) : (
              <div
                className="absolute inset-0 z-50 flex flex-col justify-end lg:justify-between"
                style={{
                  backgroundColor: visualIdentity?.theme === 'LIGHT' ? '#C5C5CA' : '#6D6E7A',
                }}
              >
                <div className="flex-1 hidden compact-landscape:flex lg:flex items-center justify-center px-4">
                  <QuizCard
                    quiz={publicQuiz}
                    visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
                    isAttendee
                    webinarId={webinarId}
                    sessionId={publicSession.session.id}
                    authorization={attendeeAuthorization}
                    sessionIdentifier={attendeeSessionIdentifier}
                    liveParticipantCountSync={liveParticipantCountSync}
                  />
                </div>

                <div className="w-full cursor-not-allowed relative flex items-center justify-center h-12 px-2 bg-white rounded-t-[20px]">
                  <p className="text-tertiary text-base">{t('live.chat.quizDisabled')}</p>
                  <div className="absolute right-4 flex items-center justify-center">
                    <SendIcon size={24} className="text-tertiary" />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

      {(isAttendee || isSpeaker) && webinarId != null && (
        <div className="p-2 hidden compact-landscape:block lg:block">
          <SharedResourceViewer
            webinarId={webinarId}
            sessionIdentifier={attendeeSessionIdentifier ?? ''}
            authorization={attendeeAuthorization ?? ''}
            visualIdentity={visualIdentity as WorkspaceVisualIdentity}
          />
        </div>
      )}

      {/* Messages épinglés en haut. Borné à un tiers du panneau : au-delà, le bloc mangeait la
          zone de lecture et repoussait la liste à chaque épinglage. */}
      <div className="shrink-0 max-h-[33%] overflow-y-auto">
        {pinnedMessagesList.length > 0 && (
          <div className="space-y-1 py-2 shadow-2xl">
            {pinnedMessagesList.map((msg: Message) => {
              const msgData: any = msg.data;
              const messageId = getMessageIdFromData(msgData)?.toString();
              const repliedToMessageId = msgData?.repliedToMessageId;
              const repliedToMessage =
                repliedToMessageId != null
                  ? messages.find((m) => {
                      const msgId = getMessageIdFromData(m.data)?.toString();
                      return msgId === repliedToMessageId.toString();
                    })
                  : undefined;
              const reactions =
                messageId && reactionsByMessage[messageId]
                  ? reactionsByMessage[messageId]
                  : undefined;
              return (
                <MessageView
                  key={msg.id}
                  message={msg}
                  isAdmin={Boolean(isAdmin)}
                  isAttendee={Boolean(isAttendee)}
                  currentUserId={userId}
                  reactions={reactions}
                  onReact={handleReaction}
                  onPinMessage={handlePinMessage}
                  isPinned={true}
                  onDeleteMessage={handleDeleteMessage}
                  onStartReply={handleStartReply}
                  onPrivateStartReply={handlePrivateStartReply}
                  isReplyTarget={
                    replyingTo != null && messageId != null && replyingTo === messageId
                  }
                  isPrivateReply={isPrivateReply}
                  replyText={replyText}
                  onChangeReplyText={(text) => setReplyText(clampChatMessageContent(text))}
                  onCancelReply={handleCancelReply}
                  onSendReply={handleSendReply}
                  repliedToMessage={repliedToMessage}
                  onBlockUser={handleBlockUser}
                  blockingMessageId={blockingMessageId}
                  onConfirmBlock={handleConfirmBlock}
                  onCancelBlock={handleCancelBlock}
                  visualIdentity={visualIdentity as WorkspaceVisualIdentity}
                  isSending={isSending}
                  isReactionDisabled={
                    reactionCooldownUntil != null && Date.now() < reactionCooldownUntil
                  }
                  onMoveToQuestions={handleMoveToQuestions}
                  onValidateQuestion={handleValidateQuestion}
                  onOfferLinkClick={handleOfferLinkClick}
                />
              );
            })}
          </div>
        )}
      </div>

      <div
        ref={messagesContainerRef}
        className=" pt-2  flex-1  overflow-y-auto min-h-0 space-y-2 flex flex-col-reverse relative"
        style={{ scrollbarColor: 'currentColor transparent' }}
      >
        {/* Messages normaux en bas */}
        <div ref={messagesContentRef} className="flex flex-col space-y-2 justify-end flex-1">
          {unpinnedMessagesList.length > 0 ? (
            unpinnedMessagesList.map((msg: Message, index: number) => {
              const msgData: any = msg.data;
              const messageId = getMessageIdFromData(msgData)?.toString();
              const repliedToMessageId = msgData?.repliedToMessageId;
              const repliedToMessage =
                repliedToMessageId != null
                  ? messages.find((m) => {
                      const msgId = getMessageIdFromData(m.data)?.toString();
                      return msgId === repliedToMessageId.toString();
                    })
                  : undefined;
              const reactions =
                messageId && reactionsByMessage[messageId]
                  ? reactionsByMessage[messageId]
                  : undefined;
              const isLast = index === unpinnedMessagesList.length - 1;
              const messageEl = (
                <MessageView
                  key={msg.id}
                  message={msg}
                  isAdmin={Boolean(isAdmin)}
                  isAttendee={Boolean(isAttendee)}
                  currentUserId={userId}
                  reactions={reactions}
                  onReact={handleReaction}
                  onPinMessage={handlePinMessage}
                  isPinned={false}
                  onDeleteMessage={handleDeleteMessage}
                  onStartReply={handleStartReply}
                  onPrivateStartReply={handlePrivateStartReply}
                  isReplyTarget={
                    replyingTo != null && messageId != null && replyingTo === messageId
                  }
                  isPrivateReply={isPrivateReply}
                  replyText={replyText}
                  onChangeReplyText={(text) => setReplyText(clampChatMessageContent(text))}
                  onCancelReply={handleCancelReply}
                  onSendReply={handleSendReply}
                  repliedToMessage={repliedToMessage}
                  onBlockUser={handleBlockUser}
                  blockingMessageId={blockingMessageId}
                  onConfirmBlock={handleConfirmBlock}
                  onCancelBlock={handleCancelBlock}
                  visualIdentity={visualIdentity as WorkspaceVisualIdentity}
                  isSending={isSending}
                  isReactionDisabled={
                    reactionCooldownUntil != null && Date.now() < reactionCooldownUntil
                  }
                  onMoveToQuestions={handleMoveToQuestions}
                  onValidateQuestion={handleValidateQuestion}
                  onOfferLinkClick={handleOfferLinkClick}
                />
              );
              return (
                <div
                  key={msg.id}
                  data-message-id={messageId}
                  ref={isLast ? lastMessageRef : undefined}
                >
                  {messageEl}
                </div>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 w-full h-full">
              <MessageCircle size={28} className="text-tertiary" />
              <p className="text-tertiary text-base text-center font-bold">{tChat('welcome')}</p>

              <p className="text-tertiary text-base">{tChat('welcomeSubtitle')}</p>
            </div>
          )}
        </div>
      </div>
      {/* send msg */}
      {!isSpeaker && (
        <div className="pt-2 shrink-0 flex flex-col gap-1">
          {showFollowLiveButton && (
            <div className="flex justify-center px-2">
              <button
                type="button"
                onClick={() => scrollToBottom()}
                aria-label={tChat('followLive')}
                className={cn(
                  'py-1.5 px-3 text-xs font-bold rounded-full inline-flex items-center gap-1.5 shadow-sm hover:opacity-90 transition-opacity cursor-pointer',
                  visualIdentity?.defaultIdentity === true
                    ? visualIdentity?.theme === 'LIGHT'
                      ? 'text-black'
                      : 'text-white'
                    : visualIdentity?.theme === 'LIGHT'
                      ? 'text-tertiary'
                      : 'text-tertiary-inverse',
                )}
                style={{
                  backgroundColor:
                    visualIdentity?.theme === 'LIGHT'
                      ? '#FFFFFF'
                      : visualIdentity?.backgroundColor || '#FFFFFF',
                }}
              >
                <ChevronDown className="size-3.5 shrink-0" aria-hidden />
                {tChat('followLive')}
              </button>
            </div>
          )}
          <div
            className={cn(
              'w-full flex items-center justify-between overflow-auto px-4 rounded-t-[20px]',
              visualIdentity?.defaultIdentity === true
                ? 'border-none'
                : 'border-t border-l border-r',
            )}
            style={{
              backgroundColor:
                visualIdentity?.theme === 'LIGHT'
                  ? '#FFFFFF'
                  : visualIdentity?.backgroundColor || '#FFFFFF',
              borderColor: visualIdentity?.contrastColor || '#FFFFFF',
            }}
          >
            {isAttendeeWriteLocked ? (
              <>
                {/* Pas de champ monté du tout : ni saisie ni envoi possibles tant que le
                    présentateur n'a pas rouvert le chat. */}
                <p
                  className={cn(
                    'w-full h-16 py-2 flex items-center cursor-not-allowed',
                    composerTextClassName,
                  )}
                >
                  {tChat('writeDisabled')}
                </p>
                <span
                  className="opacity-50"
                  style={{ color: visualIdentity?.contrastColor || '#FFFFFF' }}
                  aria-hidden
                >
                  <SendIcon size={24} />
                </span>
              </>
            ) : (
              <>
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(clampChatMessageContent(e.target.value))}
                  maxLength={CHAT_MESSAGE_MAX_LENGTH}
                  onKeyDown={handleKeyDown}
                  placeholder={tChat('writeHere')}
                  className={cn(
                    'w-full h-16 py-2 resize-none focus:outline-none ',
                    composerTextClassName,
                  )}
                  style={{ scrollbarWidth: 'none' }}
                />
                <button
                  type="button"
                  className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isSending) handleSendMessage();
                  }}
                  disabled={!inputMessage.trim() || isSending}
                  aria-label={'send'}
                >
                  <span style={{ color: visualIdentity?.contrastColor || '#FFFFFF' }}>
                    <SendIcon size={24} />
                  </span>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

{
  /* {Array.from({ length: 50 }, (_, i) => (
          <div key={i} className="p-4 border-b border-gray-200">
            <div className="font-semibold">Message {i + 1}</div>
            <div className="text-sm text-gray-600">
              Contenu du message {i + 1} pour tester le scroll. Lorem ipsum dolor sit amet,
              consectetur adipiscing elit.
            </div>
          </div>
        ))} */
}
